import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  Crosshair,
  X,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Pencil,
  Receipt,
} from 'lucide-react';
import { parseTicker } from '../../lib/kalshiTicker';
import { useAuthStore } from '../../stores/authStore';
import { BotZones } from '../../components/bots/BotZones';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { usePositionPnl } from '../../hooks/ui/usePositionPnl';
import { useThrottledMarketVersion } from '../../hooks/ui/useThrottledMarketVersion';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { OrgTradersTable, type OrgTraderRow } from '../../components/organisms/OrgTradersTable';
import { OrgSwitcher } from '../../components/OrgSwitcher';
import { useOrgScope } from '../../stores/orgScope';
import { ExpandedGamePanel } from '../ValueDashboard/index';
import { PositionSidebar } from '../ValueDashboard/PositionSidebar';
import type { TradeIntent } from '../ValueDashboard/GameDetailPanel';
import { VenueChip } from '../../components/atoms/VenueChip';
import { GameDetailLink } from '../../components/atoms/GameDetailLink';
import { OrderbooksPausedBadge } from '../../components/atoms/OrderbooksPausedBadge';
import { ConfirmModal } from '../../components/atoms/Modal';
import { SortableTh, type SortDirection } from '../../components/atoms/SortableTh';
import { EditOrderPriceModal, OrderActionButtons } from '../../components/molecules';
import { describePolyOrderCached } from '../../lib/polymarket/describeOrder';
import { usePolymarketRelayOrders } from '../../hooks/usePolymarketRelayOrders';
import { usePolymarketRelayPositions } from '../../hooks/usePolymarketRelayPositions';
import type { PolyRelayPosition, PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import type { PositionWithPnl } from '../../hooks/ui/usePositionPnl';
import type { KalshiOrder } from '../../types/kalshi';
import { describeMarket } from '../../lib/kalshiTicker';
import { SPORT_TABS, type Sport, type GameRow, type TeamSide } from '../ValueDashboard/types';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { EditPositionModal, type EditPositionData } from './EditPositionModal';
import {
  playQueueWarningTone,
  playQueueCriticalTone,
  playFellBehindTone,
  playDivergenceTone,
} from '../../lib/audio/tradeAudio';
import { readSoundSettings } from '../../lib/audio/soundSettings';
import {
  bookMid,
  computeDivergence,
  divergenceRingTickers,
  stepDivergenceLatch,
  POLY_STALE_MS,
  type DivergenceInfo,
} from '../../lib/divergence/polyKalshiDivergence';
import {
  sideNativeProbCents,
  UPOddsValue,
  type UPOdds,
} from '../../components/cards/upOdds';
import { useDisplaySettings } from '../../stores/displaySettings';
import { readSessionOrderSort, writeSessionOrderSort } from '../../lib/trading/positionsOrderSortSession';
import { apiFetch } from '../../stores/authStore';
import { cancelPolyOrderViaRelay } from '../../lib/polymarket/relay';
import {
  CardGrid,
  CompactListGrid,
  BetTeamTitle,
  InfoTooltip,
  SummaryStrip,
  SummaryTile,
  ViewModeToggle,
} from '../../components/cards/cardKit';
import { fmtUsd, fmtUsdSigned } from '../../components/cards/cardFormat';
import { useHeadroom, bumpHeadroom } from '../../hooks/useHeadroom';
import type { OrdersSyncState } from '../../stores/accountStore';
import { OrdersHelp, PositionsHelp } from '../../components/cards/helpContent';
import { OrderCard } from '../../components/cards/OrderCard';
import { CompactPositionItem, PositionCard } from '../../components/cards/PositionCard';
import { DivergenceBadge } from '../../components/cards/DivergenceBadge';
import {
  buildKalshiOrderVM,
  buildPolyOrderVM,
  openOrderEconomics,
  queueBehindAtPriceLevel,
  queueBehindTone,
  queueRowTone,
  queueTone,
  queueValuePulse,
  type OrderCardVM,
} from '../../components/cards/orderCardModel';
import {
  effectivePriceForFeeAdjustedOdds,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { useFeesReady } from '../../services/hydraFees';
import {
  buildKalshiPositionVM,
  buildPolyPositionVM,
  type PositionCardVM,
} from '../../components/cards/positionCardModel';
import {
  recordOrderSample,
  orderVelocity,
  pruneOrderVelocity,
} from '../../components/cards/orderVelocity';
import {
  recordPriceSample,
  prunePriceVelocity,
  priceVelocity,
  sideNativePriceDelta,
} from '../../components/cards/priceVelocity';

// Queue-position alert thresholds (lower = closer to the front of the
// resting-bid queue). Crossing each threshold from above fires a one-shot
// audio cue; the row stays tinted while the order remains at or below the
// threshold so the visual matches the audible state.
const QUEUE_WARNING_THRESHOLD = 50_000;
const QUEUE_CRITICAL_THRESHOLD = 10_000;
const WAY_BEHIND_QUEUE_ABOVE_THRESHOLD = 200_000;
// 24s (was 9.6s) — Josh asked for the queue-critical flash 2.5× longer so it's
// easier to catch (G9-and-Beyond, Jun 17). Keep in sync with the CSS animation
// duration on .animate-queue-critical-card-flash in globals.css.
const QUEUE_CRITICAL_FLASH_MS = 24_000;
// One-shot cross-venue divergence flash. Keep in sync with the CSS animation
// duration on .animate-divergence-flash in globals.css.
const DIVERGENCE_FLASH_MS = 6_000;
// Divergence recompute cadence. A 2¢ cross-venue gap doesn't need the ~30Hz
// market tick — recompute on a coarse clock so the per-sport getGames sweep
// (~10ms each) isn't run every frame in this already-heavy view. Holdings
// changes still recompute immediately (the memo also keys on the held set).
// Tradeoff: this is the alert's sampling resolution too — a gap that opens past
// 2¢ AND falls back under the re-arm floor entirely within one interval is
// never observed. For a coarse "venues disagree" heads-up that sub-second blip
// is noise, so 1s is the deliberate default (flash + sound fire from the same
// throttled effect, so they stay in lockstep — this only adds ~1s latency).
const DIVERGENCE_REFRESH_MS = 1_000;
const PNL_CARD_REFRESH_INTERVAL_MS = 10_000;
const POSITION_VIEW_MODES = ['compact', 'table', 'card', 'mini'] as const;
const SURFACE_VIEW_MODES = ['card', 'table'] as const;

// Two-prong rail prefs. `hidden` is a sticky pref (localStorage), so it
// survives reloads; `popped` is runtime only (a popped window dies on reload).
const TRACKER_HIDDEN_KEY = 'g1mt_tracker_prong_hidden';
const FILLS_HIDDEN_KEY = 'g1mt_fills_prong_hidden';
const POPUP_FEATURES =
  'toolbar=no,menubar=no,location=no,status=no,scrollbars=yes,resizable=yes';
const TRACKER_POPUP_FEATURES = `width=420,height=720,${POPUP_FEATURES}`;
const FILLS_POPUP_FEATURES = `width=420,height=640,${POPUP_FEATURES}`;

function readBoolPref(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

// Tri-state per sport (Prof, Jun 6): absent → not filtered on; 'include' →
// row's sport must be in the include set; 'exclude' → hide this sport.
// Empty map = all sports.
type SportFilterMode = 'include' | 'exclude';
type SportFilter = ReadonlyMap<Sport, SportFilterMode>;

// Data the EditOrderPriceModal needs to render and the venue-aware replace
// callback needs to dispatch. Built fresh each time a row's "Edit price"
// icon is clicked from a row or card.
interface EditOrderData {
  venue: 'kalshi' | 'polymarket';
  orderId: string;
  marketLabel: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  remainingCount: number;
  currentPriceCents: number;
  ticker?: string;
  /** Polymarket only — passed through to the relay so the replacement
   *  carries the right CLOB tick size + NEG_RISK flag. Undefined falls
   *  back to a final token lookup inside usePolymarketRelayOrders.replace,
   *  then the relay defaults. */
  tickSize?: string;
  negRisk?: boolean;
}

interface PendingCancelOrder {
  venue: 'kalshi' | 'polymarket';
  orderId: string;
  label: string;
}

// ── Helpers ─────────────────────────────────────────────────────

function formatCents(cents: number): string {
  return `${cents}¢`;
}

function PriceWithOddsInline({
  cents,
  odds,
  muted = false,
}: {
  cents: number | null;
  odds: string | null;
  muted?: boolean;
}) {
  if (cents == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className={muted ? 'text-muted-foreground' : 'text-foreground'}>
      {formatCents(cents)}
      {odds && <span className="ml-1.5 text-[10px] opacity-80">{odds}</span>}
    </span>
  );
}

// Delegate to the comma-grouping card formatter — traders read five-figure
// P&L here and "+$20282.62" without separators caused real misreads
// (Prof, Jun 5: "all 3 of these boxes could take a comma").
function formatDollars(cents: number): string {
  return fmtUsdSigned(cents / 100);
}

function coerceSport(raw: string | null | undefined): Sport | null {
  if (!raw) return null;
  const sport = raw.toLowerCase();
  if (SPORT_TABS.some((tab) => tab.id === sport)) return sport as Sport;
  if (sport === 'ncaa' || sport === 'ncaamb' || sport === 'ncaaw' || sport === 'ncaawb') {
    return 'cbb';
  }
  return null;
}

function sportFromTicker(ticker: string): Sport | null {
  const parsed = parseTicker(ticker);
  if (parsed.category === 'polymarket') {
    const seriesSport = parsed.series.startsWith('PM-') ? parsed.series.slice(3) : null;
    return coerceSport(seriesSport);
  }

  const categoryPrefix = parsed.category.split('-')[0];
  if (categoryPrefix === 'ncaa') return 'cbb';
  if (categoryPrefix === 'tennis') {
    const series = parsed.series.toUpperCase();
    if (series.includes('ITFW')) return 'wta';
    if (series.includes('ITF')) return 'atp';
    if (series.includes('WTA')) return 'wta';
    if (series.includes('ATP')) return 'atp';
    return null;
  }
  const categorySport = coerceSport(categoryPrefix);
  if (categorySport) return categorySport;

  const upper = ticker.toUpperCase();
  if (upper.startsWith('KXWNB')) return 'wnba';
  if (upper.startsWith('KXNBA')) return 'nba';
  if (upper.startsWith('KXNHL')) return 'nhl';
  if (upper.startsWith('KXMLB')) return 'mlb';
  if (upper.startsWith('KXNCAA')) return 'cbb';
  if (upper.startsWith('KXATP')) return 'atp';
  if (upper.startsWith('KXWTA')) return 'wta';
  if (upper.startsWith('KXUFC')) return 'ufc';
  if (upper.startsWith('PM-')) return coerceSport(upper.split('-')[1]);
  return null;
}

function sportMatchesFilter(sport: Sport | null, filter: SportFilter): boolean {
  if (filter.size === 0) return true;
  const mode = sport != null ? filter.get(sport) : undefined;
  if (mode === 'exclude') return false;
  let hasInclude = false;
  for (const m of filter.values()) {
    if (m === 'include') {
      hasInclude = true;
      break;
    }
  }
  // Exclude-only mode shows everything that isn't excluded — including rows
  // whose sport can't be derived from the ticker (they aren't the excluded
  // sport, so hiding them would be wrong).
  return hasInclude ? mode === 'include' : true;
}

// User-local short time stamp for event start — e.g. "4:05p" for a 7:05p ET
// game viewed from PST. Mirrors marketStore.formatStartTimeLocal (which
// drives the dashboard time pill) so the two surfaces read identically.
// Returns '' for unknown / +Infinity so callers can no-op concatenate.
const _LOCAL_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
// Build a GameDetail deep-link from a position/order ticker. Pass through
// `?ticker=…&side=…` so GameDetail can pre-expand the right line and
// pre-fill the trade ticket on landing. Returns null when the ticker
// doesn't parse cleanly (rare — non-game tickers).
/** Same alpha-sort collapse GameDetail's private normalizeKey applies —
 *  hydra's canonicalGameKey and marketStore row keys can disagree on team
 *  order, so compare both through the sorted form. */
function normalizeGameKey(key: string): string {
  const parts = key.split('|');
  if (parts.length === 4) {
    const [, date, a, b] = parts;
    const [teamA, teamB] = [a, b].sort();
    return `${date}|${teamA}|${teamB}`;
  }
  if (parts.length === 3) {
    const [date, a, b] = parts;
    const [teamA, teamB] = [a, b].sort();
    return `${date}|${teamA}|${teamB}`;
  }
  return key;
}

/** Resolve a position/order ticker to the live moneyline GameRow + the
 *  trade-intent prefill for the in-page buy panel (Joshua's ask: click a
 *  game on the tracker → the dashboard's buying window opens on the right).
 *
 *  Sport scope comes from the market's canonicalGameKey prefix, so each
 *  click scans ONE sport's rows, not all of them. Settled rows
 *  (gameState 'final') are skipped on purpose: their books are dead and
 *  orders would be rejected, so the caller's GameDetail-page fallback
 *  (which renders archived games) is the right surface.
 *
 *  Kalshi slots match by exact ticker → team side + held order side.
 *  Polymarket needs token matching instead: buildPolyMoneylinePair puts
 *  the SAME slug ticker on both team slots, and the canonical-yes team can
 *  land on either, so picking a slot by ticker alone arms the OPPOSITE
 *  token ~half the time (review catch). We match the HELD token against
 *  each slot's tokenId/noTokenId so the prefilled ticket always adds to
 *  the trader's actual exposure.
 *
 *  Returns null when the market/game isn't resolvable from the live store
 *  — caller falls back to the GameDetail page. */
/** Sport tabs to scan for a canonicalGameKey. The key's sport prefix is
 *  LOWERCASE ('nba|2026-06-13|NYK|SAS'), same casing as g1 Sport ids, so a
 *  direct compare works. Tennis is unified: hydra prefixes both tours as
 *  'tennis|', which maps to the ATP + WTA tabs. Unknown prefixes fall back
 *  to scanning everything — correctness over scan cost. Exported for
 *  tests. */
export function sportsForCanonicalKey(ck: string | null | undefined): Sport[] {
  const all = SPORT_TABS.map((t) => t.id as Sport);
  const prefix =
    ck && ck.split('|').length === 4 ? ck.split('|')[0].toLowerCase() : null;
  if (prefix == null) return all;
  const scoped = all.filter((sp) =>
    prefix === 'tennis' ? sp === 'atp' || sp === 'wta' : sp === prefix,
  );
  return scoped.length > 0 ? scoped : all;
}

function resolveBuyPanelTarget(
  ticker: string,
  posSide: 'yes' | 'no',
): { game: GameRow; intent: TradeIntent } | null {
  const store = useMarketStore.getState();
  const mkt = store.getMarket(ticker);
  if (!mkt) return null;
  const ck = mkt.canonicalGameKey;
  const sports = sportsForCanonicalKey(ck);
  const want = ck ? normalizeGameKey(ck) : null;
  const heldToken = posSide === 'yes' ? mkt.yesTokenId : mkt.noTokenId;

  let keyHit: GameRow | null = null;
  for (const sp of sports) {
    const games = store.getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
    for (const g of games) {
      if (g.gameState === 'final') continue;
      if (g.kalshi?.away.marketTicker === ticker) {
        return { game: g, intent: { side: 'away', venue: 'kalshi', initialOrderSide: posSide, postOnly: false } };
      }
      if (g.kalshi?.home.marketTicker === ticker) {
        return { game: g, intent: { side: 'home', venue: 'kalshi', initialOrderSide: posSide, postOnly: false } };
      }
      if (heldToken && g.polymarket && (g.polymarket.away.marketTicker === ticker || g.polymarket.home.marketTicker === ticker)) {
        for (const side of ['away', 'home'] as const) {
          const book = g.polymarket[side];
          if (book.tokenId === heldToken) {
            return { game: g, intent: { side, venue: 'polymarket', initialOrderSide: 'yes', postOnly: false } };
          }
          if (book.noTokenId === heldToken) {
            return { game: g, intent: { side, venue: 'polymarket', initialOrderSide: 'no', postOnly: false } };
          }
        }
      }
      if (keyHit == null && want != null && normalizeGameKey(g.key) === want) keyHit = g;
    }
  }
  // Spread/total/period tickers: no moneyline slot carries them, so fall
  // back to the game matched via the full-game canonicalGameKey. Panel
  // opens on the game's moneyline with the held order side preselected.
  if (keyHit) {
    return { game: keyHit, intent: { side: 'away', venue: 'kalshi', initialOrderSide: posSide, postOnly: false } };
  }
  return null;
}

function gameDetailHref(ticker: string, side: 'yes' | 'no'): string | null {
  const parsed = parseTicker(ticker);
  if (!parsed.game || !parsed.date) return null;
  const a = parsed.game.awayCode;
  const b = parsed.game.homeCode;
  return `/game/${parsed.date}/${a}/${b}?ticker=${encodeURIComponent(ticker)}&side=${side}`;
}

function formatStartTimeLocal(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '';
  const parts = _LOCAL_TIME_FMT.formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase();
  const ampm = dayPeriod.startsWith('p') ? 'p' : 'a';
  return hour && minute ? `${hour}:${minute}${ampm}` : '';
}

// Tints the "Now" (mark) cell so the trader can sweep the column for
// outliers. Buckets cover 0-99 with no gaps; null/undefined falls through
// to the muted default so a missing mark doesn't read as "in the money".
function nowColorClass(cents: number | null | undefined): string {
  if (cents == null) return 'text-muted-foreground';
  if (cents <= 15) return 'text-red-500';
  if (cents <= 25) return 'text-red-400';
  if (cents <= 35) return 'text-red-300';
  if (cents <= 45) return 'text-orange-400';
  if (cents <= 55) return 'text-yellow-400';
  if (cents <= 65) return 'text-[#9aa00d]';
  if (cents <= 75) return 'text-green-300';
  if (cents <= 85) return 'text-green-400';
  return 'text-green-500';
}

type ActiveSortDir = Exclude<SortDirection, null>;
type PositionSortKey = 'default' | 'size' | 'velocity';
type OrderSortKey = 'default' | 'size' | 'queue' | 'velocity';

/** Re-settle the orders table's row order at this cadence when sorting by a LIVE
 *  metric (queue/velocity). Sorting on a value that ticks every market update made
 *  rows leapfrog constantly ("positions spazzing"); snapshotting the sort keys on
 *  this interval keeps the order calm while the cells still render live values. */
const ORDER_SORT_STABILIZE_MS = 2000;

interface SortState<K extends string> {
  key: K;
  dir: ActiveSortDir;
}

function compareDefaultSort(
  a: { sortStart: number; sortTicker: string },
  b: { sortStart: number; sortTicker: string },
): number {
  if (a.sortStart !== b.sortStart) return a.sortStart - b.sortStart;
  return a.sortTicker.localeCompare(b.sortTicker);
}

function SortSegmentButton({
  active,
  dir,
  label,
  title,
  onClick,
}: {
  active: boolean;
  dir: ActiveSortDir;
  label: string;
  title: string;
  onClick: () => void;
}) {
  const Icon = dir === 'asc' ? ArrowUpWideNarrow : ArrowDownWideNarrow;
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex h-7 items-center gap-1.5 px-2 text-[11px] font-medium transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-300'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <span>{label}</span>
      {active && <Icon size={12} />}
    </button>
  );
}

/** Amber pill when the open-orders refresh loop is stale or failing.
 *  Child component so its 5s aging tick doesn't re-render the whole page.
 *  Jun 11 incident: the loop failed silently for minutes while a trader
 *  re-placed "missing" (actually resting) orders — staleness must be
 *  visible the moment the list can no longer be trusted. */
function formatSyncAge(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 120) return `${s}s`;
  return `${Math.round(s / 60)}m`;
}

function OrdersSyncBadge({
  sync,
  active,
  onRetry,
}: {
  sync: OrdersSyncState;
  /** Any non-disconnected session state — the badge must stay visible
   *  through 'connecting'/'error' wedges (that's exactly when the list
   *  can't be trusted), hiding only on a deliberate disconnect. */
  active: boolean;
  onRetry: () => void;
}) {
  const mountMsRef = useRef(Date.now());
  const [throttledUntil, setThrottledUntil] = useState(0);
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 5_000);
    return () => window.clearInterval(id);
  }, []);
  if (!active) return null;
  const baseline = sync.lastSuccessMs ?? mountMsRef.current;
  const ageMs = Date.now() - baseline;
  // >=2 consecutive failures: a single blip at the 5s poll cadence heals on
  // the next tick — surfacing it would train traders to ignore the badge.
  const failing = sync.consecutiveFailures >= 2;
  if (!failing && ageMs < 30_000) return null;
  const throttled = Date.now() < throttledUntil;
  const label = failing
    ? `Orders sync failing (${sync.consecutiveFailures}×) — retry`
    : sync.lastSuccessMs === null
      ? 'Orders not synced yet — retry'
      : `Orders synced ${formatSyncAge(ageMs)} ago — retry`;
  return (
    <button
      onClick={() => {
        if (throttled) return;
        setThrottledUntil(Date.now() + 3_000);
        onRetry();
      }}
      disabled={throttled}
      className="border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
      title={
        failing
          ? `Last error: ${sync.lastError ?? 'unknown'} — click to retry now`
          : 'The order list has not refreshed recently — click to retry now'
      }
    >
      <RefreshCw size={11} />
      {label}
    </button>
  );
}

/** Loud banner for the exact Jun 11 failure shape: the relay's intent
 *  tracker says contracts are resting (orders accepted at the venue) but
 *  the list below is EMPTY. Without this, the natural trader response is
 *  to re-place — stacking live exposure on invisible orders. Hysteresis:
 *  only shows after the mismatch persists 10s, so the normal
 *  place→list-catches-up gap never flashes it. */
function OrdersMismatchBanner({
  intentContracts,
  listedCount,
  syncFailures,
  syncHealthy,
  active,
  onRefresh,
}: {
  intentContracts: number;
  /** UNFILTERED unified order count (kalshi + poly) — never the
   *  sport-filtered list, or an include/exclude chip that hides every
   *  order fires a false "out of sync" alarm in a healthy session and
   *  teaches traders to ignore the one banner that must be trusted. */
  listedCount: number;
  /** consecutiveFailures from ordersSync — keeps the banner alive past the
   *  5-min intent TTL when the refresh loop itself is persistently down. */
  syncFailures: number;
  /** True when the orders fetch loop is demonstrably healthy (fresh
   *  success, zero failure streak). Flips the copy from "list is likely
   *  out of sync" to the venue-truth message: during the Jun 12 mass-
   *  cancel sweep the list was CORRECT (orders really were canceled at
   *  Kalshi) and the out-of-sync wording sent traders chasing a refresh
   *  bug that wasn't there (grim's screenshot). */
  syncHealthy: boolean;
  active: boolean;
  onRefresh: () => void;
}) {
  const firstSeenRef = useRef<number | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 2_500);
    return () => window.clearInterval(id);
  }, []);
  const mismatch =
    active && listedCount === 0 && (intentContracts > 0 || syncFailures >= 4);
  useEffect(() => {
    if (mismatch && firstSeenRef.current === null) firstSeenRef.current = Date.now();
    if (!mismatch) firstSeenRef.current = null;
  }, [mismatch]);
  if (!mismatch) return null;
  if (firstSeenRef.current === null || Date.now() - firstSeenRef.current < 10_000) return null;
  return (
    <div className="border-amber-500/50 bg-amber-500/10 flex items-center justify-between gap-3 rounded-lg border px-4 py-3">
      <div className="text-amber-300 light:text-amber-700 text-sm">
        <span className="font-semibold">
          {intentContracts > 0
            ? `Your account shows ${intentContracts.toLocaleString('en-US')} contracts in recent resting buys, but no orders are displayed.`
            : 'The order list has failed to refresh repeatedly and may be missing your orders.'}
        </span>{' '}
        {syncHealthy
          ? 'Kalshi reports no resting orders for this account — recent buys were likely canceled at the venue or already filled. Check Fills before re-placing.'
          : "The list is likely out of sync — refresh before assuming an order didn't go through, and don't re-place orders you've already submitted."}
      </div>
      <button
        onClick={onRefresh}
        className="border-amber-500/60 bg-amber-500/20 text-amber-200 light:text-amber-800 hover:bg-amber-500/30 shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors"
      >
        Refresh orders
      </button>
    </div>
  );
}

function SportFilterChip({
  mode,
  label,
  onClick,
  cycleHint,
}: {
  mode: 'off' | 'include' | 'exclude';
  label: string;
  onClick: () => void;
  cycleHint?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={mode !== 'off'}
      title={
        cycleHint
          ? `Click: only ${label} → exclude ${label} → back to all`
          : undefined
      }
      className={`inline-flex h-7 items-center rounded-md border px-2.5 text-[11px] font-medium transition-colors ${
        mode === 'include'
          ? 'border-blue-500/50 bg-blue-500/20 text-blue-300'
          : mode === 'exclude'
            ? 'border-red-500/50 bg-red-500/20 text-red-400 line-through'
            : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {/* Exclude is conveyed by the red strikethrough alone (matches the org
          page). No X glyph — it widened the chip on the exclude toggle. */}
      {label}
    </button>
  );
}

function RefreshProgress({
  anchorMs,
  intervalMs,
  refreshing,
}: {
  anchorMs: number;
  intervalMs: number;
  refreshing: boolean;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const elapsed = ((nowMs - anchorMs) % intervalMs + intervalMs) % intervalMs;
  const progress = Math.min(1, Math.max(0, elapsed / intervalMs));
  const remainingSeconds = Math.max(1, Math.ceil((intervalMs - elapsed) / 1000));
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <div
      className="text-muted-foreground inline-flex items-center gap-1 text-[10px] font-medium leading-none"
      title={`Next P&L refresh in ${remainingSeconds}s`}
      aria-label={`Next P&L refresh in ${remainingSeconds} seconds`}
    >
      <svg className="h-4 w-4 -rotate-90" viewBox="0 0 20 20" aria-hidden="true">
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.18"
          strokeWidth="2"
        />
        <circle
          cx="10"
          cy="10"
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span className="min-w-[2ch] text-right font-mono">
        {refreshing ? '...' : `${remainingSeconds}`}
      </span>
    </div>
  );
}

// ── Public component (role dispatcher) ──────────────────────────

interface PositionTrackerProps {
  onSelectTrader?: (row: OrgTraderRow) => void;
  onSelectOrg?: (row: OrgSummaryRow) => void;
}

export function PositionTracker({ onSelectTrader }: PositionTrackerProps = {}) {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  if (role === 'manager') {
    return (
      <ManagerPositionsView
        currentUserId={userId}
        onSelectTrader={onSelectTrader}
      />
    );
  }
  if (role === 'admin') {
    return (
      <AdminPositionsView
        currentUserId={userId}
        onSelectTrader={onSelectTrader}
      />
    );
  }
  return <TraderView />;
}

// ── Trader view (own data only) ─────────────────────────────────

// `footer` slots extra content (the org-roster table for manager/admin) into
// the END of the MAIN content column — beside the viewport-tall sticky rail,
// not as a detached block below it — so it flows with positions/orders instead
// of stranding a gap under the rail. Bare <TraderView/> (traders, popouts)
// passes nothing → renders nothing extra.
function TraderView({ footer }: { footer?: ReactNode } = {}) {
  const navigate = useNavigate();
  const status = useAccountStore((s) => s.status);
  const balance = useAccountStore((s) => s.balance);
  const openOrders = useAccountStore((s) => s.openOrders);
  const recentFills = useAccountStore((s) => s.recentFills);
  const keyMode = useAccountStore((s) => s.keyMode);
  const realizedPnlCents = useAccountStore((s) => s.realizedPnlCents);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const replaceOrder = useAccountStore((s) => s.replaceOrder);
  const refreshPositions = useAccountStore((s) => s.refreshPositions);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const ordersSync = useAccountStore((s) => s.ordersSync);
  const accountStatus = useAccountStore((s) => s.status);
  const headroom = useHeadroom();
  const refreshPnl = useAccountStore((s) => s.refreshPnl);

  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const orderViewMode = useDisplaySettings((s) => s.orderViewMode);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);
  const setOrderViewMode = useDisplaySettings((s) => s.setOrderViewMode);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const feeAdjustedOdds = useDisplaySettings((s) => s.feeAdjustedOdds);
  const showPnl = useDisplaySettings((s) => s.showPnl);
  const toggleShowPnl = useDisplaySettings((s) => s.toggleShowPnl);
  // MM Program rebate isn't folded into post-trade position / open-order
  // economics yet (needs per-order live/pregame state). Pre-trade surfaces
  // (trade ticket + ladder) carry it. See [[project_kalshi_mm_rebate_model]].
  const makerRebatePct = 0;
  const feesReady = useFeesReady();
  // The position tracker — positions + open orders + recent fills — docks as a
  // content-pushing sticky sidebar on the right (Dan's value-dashboard format,
  // rendered in-page rather than as a floating overlay). Default visible;
  // toggled from the header; shares the right slot with the buy panel.
  // Two-prong rail: the tracker (P&L + positions + orders) and recent fills
  // each dock in the rail, pop out into their own window, or hide. A prong is
  // DOCKED when it's neither hidden nor popped; the rail collapses (and the
  // tables go full-width) only when neither prong is docked.
  const [trackerHidden, setTrackerHidden] = useState(() => readBoolPref(TRACKER_HIDDEN_KEY));
  const [fillsHidden, setFillsHidden] = useState(() => readBoolPref(FILLS_HIDDEN_KEY));
  const [trackerPopped, setTrackerPopped] = useState(false);
  const [fillsPopped, setFillsPopped] = useState(false);
  const trackerWinRef = useRef<Window | null>(null);
  const fillsWinRef = useRef<Window | null>(null);

  useEffect(() => {
    localStorage.setItem(TRACKER_HIDDEN_KEY, String(trackerHidden));
  }, [trackerHidden]);
  useEffect(() => {
    localStorage.setItem(FILLS_HIDDEN_KEY, String(fillsHidden));
  }, [fillsHidden]);

  // Re-dock a prong when its popped-out window closes. Poll because the opener
  // gets no cross-window 'closed' event to subscribe to.
  useEffect(() => {
    if (!trackerPopped && !fillsPopped) return;
    const id = setInterval(() => {
      if (trackerPopped && trackerWinRef.current?.closed) {
        trackerWinRef.current = null;
        setTrackerPopped(false);
      }
      if (fillsPopped && fillsWinRef.current?.closed) {
        fillsWinRef.current = null;
        setFillsPopped(false);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [trackerPopped, fillsPopped]);

  const popTracker = useCallback(() => {
    const w = window.open('/tracker-popup', 'g1-tracker-popup', TRACKER_POPUP_FEATURES);
    if (!w) return; // popup blocked — leave it docked
    trackerWinRef.current = w;
    w.focus();
    setTrackerPopped(true);
  }, []);
  const popFills = useCallback(() => {
    const w = window.open('/fills', 'g1-fills-popup', FILLS_POPUP_FEATURES);
    if (!w) return;
    fillsWinRef.current = w;
    w.focus();
    setFillsPopped(true);
  }, []);

  const trackerDocked = !trackerHidden && !trackerPopped;
  const fillsDocked = !fillsHidden && !fillsPopped;

  // Toggling a prong's rail visibility. Hiding one that's currently popped also
  // dismisses its window, so a "hidden" prong never strands a live, P&L-render-
  // ing window the trader can only close via OS chrome. (Uses the win ref, not
  // the popped flag, to stay fresh without a dep array.)
  const toggleTrackerHidden = () => {
    if (!trackerHidden && trackerWinRef.current && !trackerWinRef.current.closed) {
      trackerWinRef.current.close();
      trackerWinRef.current = null;
      setTrackerPopped(false);
    }
    setTrackerHidden((v) => !v);
  };
  const toggleFillsHidden = () => {
    if (!fillsHidden && fillsWinRef.current && !fillsWinRef.current.closed) {
      fillsWinRef.current.close();
      fillsWinRef.current = null;
      setFillsPopped(false);
    }
    setFillsHidden((v) => !v);
  };
  const [positionSort, setPositionSort] = useState<SortState<PositionSortKey>>({
    key: 'default',
    dir: 'asc',
  });
  const [orderSort, setOrderSort] = useState<SortState<OrderSortKey>>(() => {
    // Session pick first so an involuntary remount (reconnect/auth blip) keeps
    // the trader's live sort; fall back to the persisted landing default. The
    // session store allows queue/velocity (the default store coerces them away),
    // so live keys must seed from session, never from the default.
    const session = readSessionOrderSort();
    if (session) return session;
    const saved = useDisplaySettings.getState().defaultPositionsOrderSort;
    return { key: saved.key, dir: saved.dir };
  });
  // Persist every Open Orders sort change to the per-session store so the next
  // mount (including an involuntary reconnect remount) restores this pick
  // instead of reverting to the factory default.
  useEffect(() => {
    writeSessionOrderSort(orderSort);
  }, [orderSort]);
  const [sportFilter, setSportFilter] = useState<SportFilter>(() => new Map());
  const sportFilterKey = useMemo(
    () =>
      SPORT_TABS.filter((sport) => sportFilter.has(sport.id))
        .map((sport) => `${sport.id}:${sportFilter.get(sport.id)}`)
        .join('|'),
    [sportFilter],
  );
  const handleAllSportsFilter = useCallback(() => {
    setSportFilter(new Map());
  }, []);
  // 3-click cycle per chip: off → include ("only X") → exclude (red X) → off.
  const handleSportFilterToggle = useCallback((sport: Sport) => {
    setSportFilter((prev) => {
      const next = new Map(prev);
      const cur = next.get(sport);
      if (cur === undefined) next.set(sport, 'include');
      else if (cur === 'include') next.set(sport, 'exclude');
      else next.delete(sport);
      return next;
    });
  }, []);

  const { positionsWithPnl } = usePositionPnl();

  // Last-trade price (hydra-sourced) for the Open Orders "Last" column.
  const getLastTradeYesCents = useMarketStore((s) => s.getLastTradeYesCents);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  // Cross-venue divergence: getGames does the Kalshi<->Polymarket canonical-key
  // pairing; getOrderbookAgeMs gates poly freshness so a stale feed never alerts.
  const getGames = useMarketStore((s) => s.getGames);
  const getOrderbookAgeMs = useMarketStore((s) => s.getOrderbookAgeMs);
  // Hydra orderbook for cumulative queue depth (same-side levels at strictly
  // better prices fill before this order does, so they belong in "queue").
  const getOrderbookRaw = useMarketStore((s) => s.getOrderbookRaw);
  // Event-start lookup that resolves via canonical game key — sibling
  // markets (Kalshi NO legs, spreads, totals) share the Polymarket-
  // sourced `eventStartTime` so all bets on one game sort together.
  const getEventStartTimeMsByTicker = useMarketStore((s) => s.getEventStartTimeMsByTicker);
  // The page's market clock, throttled to 4 Hz (POSITIONS_TICK_MS). Every
  // downstream memo/effect keyed on marketVersion inherits the cadence — the
  // raw ~30 Hz store counter re-ran this entire component per tick and was
  // the diagnosed main-thread saturator behind the hydra 8 MiB kill loop.
  const marketVersion = useThrottledMarketVersion();

  // Queue position lives on /api/kalshi/me/snapshot (the relay's per-user
  // shape) — it isn't carried on accountStore.openOrders. Pull it via the
  // existing snapshot hook and key by orderId.
  const orderTickers = useMemo(
    () => Array.from(new Set(openOrders.map((o) => o.ticker))),
    [openOrders],
  );
  const { ordersByTicker } = useUserKalshiPOS(orderTickers);

  // Polymarket org-key rows for the unified Open Orders / Open Positions
  // tables below. Both hooks auto-poll and silently return empty arrays
  // for traders whose org doesn't have a poly credential, so they're
  // safe to mount unconditionally — no extra availability gate needed.
  const polyOrdersState = usePolymarketRelayOrders();
  const polyPositionsState = usePolymarketRelayPositions();
  const refreshPolyOrders = polyOrdersState.refresh;
  const refreshPolyPositions = polyPositionsState.refresh;
  // Only show currently-held positions in the Open Positions table —
  // netCount=0 rows are closed-out history and surface in MyTrades.
  const polyOpenPositions = useMemo(
    () => polyPositionsState.positions.filter((p) => p.netCount > 0),
    [polyPositionsState.positions],
  );

  // Tell hydra to stream orderbook frames for the user's open-order tickers —
  // the cumulative queue calc needs the live book, and PositionTracker may
  // be opened standalone (without ValueDashboard already subscribing). Use a
  // stable key so the effect only fires when the ticker set actually changes.
  const orderTickersKey = useMemo(() => orderTickers.slice().sort().join('|'), [orderTickers]);
  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('position-tracker', orderTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('position-tracker', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderTickersKey]);

  const handlePositionSort = useCallback((key: string) => {
    const nextKey: PositionSortKey = key === 'size' ? 'size' : key === 'velocity' ? 'velocity' : 'default';
    setPositionSort((prev) => {
      if (nextKey === 'default') return { key: 'default', dir: 'asc' };
      return {
        key: nextKey,
        dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
      };
    });
  }, []);

  const handleOrderSort = useCallback((key: string) => {
    const nextKey: OrderSortKey =
      key === 'velocity'
        ? 'velocity'
        : key === 'queue'
          ? 'queue'
          : key === 'size'
            ? 'size'
            : 'default';
    setOrderSort((prev) => {
      if (nextKey === 'default') return { key: 'default', dir: 'asc' };
      if (nextKey === 'size') {
        return {
          key: nextKey,
          dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
        };
      }
      if (nextKey === 'velocity') {
        return {
          key: nextKey,
          dir: prev.key === nextKey && prev.dir === 'desc' ? 'asc' : 'desc',
        };
      }
      return {
        key: nextKey,
        dir: prev.key === nextKey && prev.dir === 'asc' ? 'desc' : 'asc',
      };
    });
  }, []);

  // Unified sort key: (eventStartTime asc, ticker asc). Same start time +
  // alpha ticker keeps all bets on one game adjacent — e.g. three moneyline
  // legs and a totals on the same Yankees game land in one block instead of
  // being scattered. Missing start times sentinel to +Infinity so they fall
  // to the end. The canonical-key resolver propagates event start to
  // sibling markets (NO legs, spreads, totals), while direct market
  // `eventStartTime` covers new Kalshi sports without a canonical key.
  // Poly relay tickers (CLOB asset ids) usually won't resolve, so they
  // sort to the tail — acceptable since Poly is moneyline-only and rarely
  // overlaps Kalshi multi-leg groupings.
  const sortStartMs = useCallback(
    (ticker: string): number => {
      void marketVersion;
      const ms = getEventStartTimeMsByTicker(ticker);
      return ms != null ? ms : Number.POSITIVE_INFINITY;
    },
    [getEventStartTimeMsByTicker, marketVersion],
  );

  type SortedPosRow =
    | {
        kind: 'kalshi';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        pos: PositionWithPnl;
        pendingSellQty: number;
      }
    | {
        kind: 'poly';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        pos: PolyRelayPosition;
      };

  const baseSortedPositions = useMemo<SortedPosRow[]>(() => {
    const rows: SortedPosRow[] = [];
    for (const pos of positionsWithPnl) {
      const sport = sportFromTicker(pos.ticker);
      if (!sportMatchesFilter(sport, sportFilter)) continue;
      let pendingSellQty = 0;
      for (const o of openOrders) {
        if (o.ticker === pos.ticker && o.side === pos.side && o.action === 'sell') {
          pendingSellQty += o.remainingCount;
        }
      }
      rows.push({
        kind: 'kalshi',
        sport,
        sortStart: sortStartMs(pos.ticker),
        sortTicker: pos.ticker,
        pos,
        pendingSellQty,
      });
    }
    for (const pos of polyOpenPositions) {
      const sport = sportFromTicker(pos.ticker);
      if (!sportMatchesFilter(sport, sportFilter)) continue;
      rows.push({
        kind: 'poly',
        sport,
        sortStart: sortStartMs(pos.ticker),
        sortTicker: pos.ticker,
        pos,
      });
    }
    rows.sort(compareDefaultSort);
    return rows;
  }, [positionsWithPnl, polyOpenPositions, openOrders, sortStartMs, sportFilter]);

  // Position "velocity" = how fast the position's MARKET price is moving (¢/min).
  // Positions have no intrinsic/queue velocity, so this samples getMarketPrice on
  // a throttled tick (same rationale as the order-sort stabilize: don't re-order
  // the list on every market tick) and reports |Δprice| over a rolling window.
  // Kalshi-only — getMarketPrice can't resolve poly condition ids, so poly
  // positions get no velocity and sort last (matches the order-sort's kalshi
  // scope). It's net displacement over the window, so a swing out-and-back within
  // it reads ~0 — intentional.
  const positionVelSamplesRef = useRef<Map<string, { ms: number; price: number }[]>>(new Map());
  const [posVelTick, setPosVelTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setPosVelTick((t) => t + 1), ORDER_SORT_STABILIZE_MS);
    return () => window.clearInterval(id);
  }, []);
  const stablePositionVelocity = useMemo(() => {
    const samples = positionVelSamplesRef.current;
    const now = Date.now();
    const WINDOW_MS = 60_000;
    const out = new Map<string, number>();
    const live = new Set<string>();
    for (const row of baseSortedPositions) {
      if (row.kind !== 'kalshi') continue; // poly: no resolvable market price → sorts last
      const ticker = row.sortTicker;
      live.add(ticker);
      const price = getMarketPrice(ticker);
      if (price == null) continue;
      const arr = samples.get(ticker) ?? [];
      arr.push({ ms: now, price });
      while (arr.length > 1 && now - arr[0].ms > WINDOW_MS) arr.shift();
      samples.set(ticker, arr);
      if (arr.length >= 2) {
        const dtMin = (arr[arr.length - 1].ms - arr[0].ms) / 60_000;
        if (dtMin > 0) out.set(ticker, Math.abs(arr[arr.length - 1].price - arr[0].price) / dtMin);
      }
    }
    for (const t of [...samples.keys()]) if (!live.has(t)) samples.delete(t);
    return out;
    // Throttled on purpose: sample + recompute only on the tick, not every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posVelTick]);

  const sortedPositions = useMemo<SortedPosRow[]>(() => {
    if (positionSort.key === 'default') return baseSortedPositions;
    const dir = positionSort.dir === 'asc' ? 1 : -1;
    const sizeOf = (row: SortedPosRow): number =>
      row.kind === 'poly' ? row.pos.netCount : Math.abs(row.pos.position);
    const velocityOf = (row: SortedPosRow): number | null =>
      stablePositionVelocity.get(row.sortTicker) ?? null;
    const rows = [...baseSortedPositions];
    rows.sort((a, b) => {
      if (positionSort.key === 'velocity') {
        const va = velocityOf(a);
        const vb = velocityOf(b);
        if (va == null && vb == null) return compareDefaultSort(a, b);
        if (va == null) return 1;
        if (vb == null) return -1;
        const d = va - vb;
        if (d !== 0) return d * dir;
        return compareDefaultSort(a, b);
      }
      const sizeDelta = sizeOf(a) - sizeOf(b);
      if (sizeDelta !== 0) return sizeDelta * dir;
      return compareDefaultSort(a, b);
    });
    return rows;
  }, [baseSortedPositions, positionSort, stablePositionVelocity]);

  type SortedOrderRow =
    | {
        kind: 'kalshi';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        order: KalshiOrder;
      }
    | {
        kind: 'poly';
        sport: Sport | null;
        sortStart: number;
        sortTicker: string;
        order: PolyRelayOpenOrder;
      };

  const baseSortedOrders = useMemo<SortedOrderRow[]>(() => {
    const rows: SortedOrderRow[] = [];
    for (const order of openOrders) {
      const sport = sportFromTicker(order.ticker);
      if (!sportMatchesFilter(sport, sportFilter)) continue;
      rows.push({
        kind: 'kalshi',
        sport,
        sortStart: sortStartMs(order.ticker),
        sortTicker: order.ticker,
        order,
      });
    }
    for (const order of polyOrdersState.orders) {
      const assetId = String(order.asset_id ?? order.market ?? order.id ?? order.orderID ?? order.orderId ?? '');
      const sport = describePolyOrderCached(assetId)?.sport ?? sportFromTicker(assetId);
      if (!sportMatchesFilter(sport, sportFilter)) continue;
      rows.push({
        kind: 'poly',
        sport,
        sortStart: sortStartMs(assetId),
        sortTicker: assetId,
        order,
      });
    }
    rows.sort(compareDefaultSort);
    return rows;
  }, [openOrders, polyOrdersState.orders, sortStartMs, sportFilter]);

  const recentFilledQtyByOrderId = useMemo(() => {
    const map = new Map<string, number>();
    for (const fill of recentFills) {
      if (!fill.orderId) continue;
      map.set(fill.orderId, (map.get(fill.orderId) ?? 0) + Math.max(0, Math.round(fill.count)));
    }
    return map;
  }, [recentFills]);

  // Per-order queue metrics (Professor's spec):
  //   queue        = atLevel + better. Your absolute spot in the global
  //                  FIFO — every contract that fills before yours.
  //   queueAbove   = better only. Contracts resting at strictly better
  //                  prices; these clear before your level is touched.
  //                  0 = top of book (you're at the best price).
  //   queueBehind  = atLevelDepth − atLevel − own remaining size.
  //                  Contracts at your exact price level resting behind you.
  //   total        = both sides combined (book depth), matching
  //                  marketStore's `bookDepth` concept. The percentile
  //                  denominator for tone helpers; we use both sides so
  //                  the color reads against total market liquidity, not
  //                  one side that may be hollow.
  // When the book hasn't loaded, queue degrades to at-level (a lower
  // bound); above/behind stay null so the cells render "—" instead of
  // misleading zeros.
  const queueMetricsByOrderId = useMemo(() => {
    void marketVersion;
    const map = new Map<
      string,
      {
        queue: number | null;
        queueAbove: number | null;
        queueBehind: number | null;
        total: number | null;
      }
    >();
    for (const order of openOrders) {
      const slices = ordersByTicker.get(order.ticker) ?? [];
      const slice = slices.find((s) => s.orderId === order.orderId);
      const atLevel = slice?.queuePosition ?? null;
      if (atLevel == null) {
        map.set(order.orderId, {
          queue: null,
          queueAbove: null,
          queueBehind: null,
          total: null,
        });
        continue;
      }
      const book = getOrderbookRaw(order.ticker);
      if (!book) {
        map.set(order.orderId, {
          queue: atLevel,
          queueAbove: null,
          queueBehind: null,
          total: null,
        });
        continue;
      }
      // The order's own price (side-native cents). Mirrors the Bid column's
      // yes_price-only fallback — Kalshi often returns just yes_price even
      // for NO orders.
      const priceCents =
        order.side === 'yes'
          ? (order.yesPrice ?? (order.noPrice != null ? 100 - order.noPrice : null))
          : (order.noPrice ?? (order.yesPrice != null ? 100 - order.yesPrice : null));
      if (priceCents == null) {
        map.set(order.orderId, {
          queue: atLevel,
          queueAbove: null,
          queueBehind: null,
          total: null,
        });
        continue;
      }
      // "Better" levels (fill before this order):
      //   • BUY  → same-side bids at strictly higher prices.
      //   • SELL → opposite-side bids at strictly higher 100-prices, which
      //            represent asks at lower prices than this sell.
      const isSell = order.action === 'sell';
      const refLevels = isSell
        ? (order.side === 'yes' ? book.no : book.yes)
        : (order.side === 'yes' ? book.yes : book.no);
      const threshold = isSell ? 100 - priceCents : priceCents;
      let better = 0;
      let atLevelDepth = 0;
      for (const lvl of refLevels) {
        if (lvl.price > threshold) better += lvl.size;
        else if (lvl.price === threshold) atLevelDepth += lvl.size;
      }
      const total =
        book.yes.reduce((s, l) => s + l.size, 0) +
        book.no.reduce((s, l) => s + l.size, 0);
      map.set(order.orderId, {
        queue: atLevel + better,
        queueAbove: better,
        // Math.max guards stale-book races where Kalshi's atLevel slot
        // outruns the book snapshot.
        queueBehind: queueBehindAtPriceLevel(atLevelDepth, atLevel, order.remainingCount),
        total,
      });
    }
    return map;
  }, [openOrders, ordersByTicker, getOrderbookRaw, marketVersion]);

  // Throttle the SORT KEYS for live-metric sorts so the orders table doesn't
  // re-order on every market tick (the "spazzing" report). Cells keep rendering
  // live values from queueMetricsByOrderId; only the row ORDER settles on the
  // ORDER_SORT_STABILIZE_MS cadence. default/size sorts are already stable.
  const [sortStabilizeTick, setSortStabilizeTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setSortStabilizeTick((t) => t + 1),
      ORDER_SORT_STABILIZE_MS,
    );
    return () => window.clearInterval(id);
  }, []);
  const stableOrderSortKeys = useMemo(() => {
    const m = new Map<string, { queue: number | null; velocity: number | null }>();
    for (const order of openOrders) {
      const queue = queueMetricsByOrderId.get(order.orderId)?.queue ?? null;
      const v = orderVelocity(order.orderId).m1;
      const velocity =
        v.filledDelta != null && v.filledDelta > 0
          ? v.filledDelta
          : v.queueDelta != null
            ? Math.abs(v.queueDelta)
            : null;
      m.set(order.orderId, { queue, velocity });
    }
    return m;
    // Throttled ON PURPOSE: recompute only on the stabilize tick (latest values at
    // that moment), NOT on every queueMetricsByOrderId/openOrders change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortStabilizeTick]);

  const sortedOrders = useMemo<SortedOrderRow[]>(() => {
    if (orderSort.key === 'default') return baseSortedOrders;
    const dir = orderSort.dir === 'asc' ? 1 : -1;
    const queueOf = (row: SortedOrderRow): number | null =>
      row.kind === 'kalshi'
        ? stableOrderSortKeys.get(row.order.orderId)?.queue ?? null
        : null;
    const velocityOf = (row: SortedOrderRow): number | null =>
      row.kind === 'kalshi'
        ? stableOrderSortKeys.get(row.order.orderId)?.velocity ?? null
        : null;
    const sizeOf = (row: SortedOrderRow): number => {
      if (row.kind === 'kalshi') return row.order.remainingCount;
      const original =
        typeof row.order.original_size === 'number'
          ? row.order.original_size
          : Number(row.order.original_size ?? 0);
      const matched =
        typeof row.order.size_matched === 'number'
          ? row.order.size_matched
          : Number(row.order.size_matched ?? 0);
      return Math.max(0, original - matched);
    };
    const rows = [...baseSortedOrders];
    rows.sort((a, b) => {
      if (orderSort.key === 'size') {
        const sizeDelta = sizeOf(a) - sizeOf(b);
        if (sizeDelta !== 0) return sizeDelta * dir;
        return compareDefaultSort(a, b);
      }
      if (orderSort.key === 'velocity') {
        const va = velocityOf(a);
        const vb = velocityOf(b);
        if (va == null && vb == null) return compareDefaultSort(a, b);
        if (va == null) return 1;
        if (vb == null) return -1;
        const velocityDelta = va - vb;
        if (velocityDelta !== 0) return velocityDelta * dir;
        return compareDefaultSort(a, b);
      }
      const qa = queueOf(a);
      const qb = queueOf(b);
      if (qa == null && qb == null) return compareDefaultSort(a, b);
      if (qa == null) return 1;
      if (qb == null) return -1;
      const queueDelta = qa - qb;
      if (queueDelta !== 0) return queueDelta * dir;
      return compareDefaultSort(a, b);
    });
    return rows;
  }, [baseSortedOrders, orderSort, stableOrderSortKeys]);

  const visibleTotalPnl = useMemo(
    () =>
      sortedPositions.reduce((sum, row) => {
        if (row.kind !== 'kalshi') return sum;
        return sum + (row.pos.unrealizedPnl ?? 0);
      }, 0),
    [sortedPositions],
  );

  const visiblePositionValueDollars = useMemo(
    () =>
      sortedPositions.reduce((sum, row) => {
        if (row.kind === 'poly') {
          return sum + (row.pos.avgEntryPriceCents * row.pos.netCount) / 100;
        }
        const valueCents =
          row.pos.totalValueCents ?? row.pos.averagePrice * Math.abs(row.pos.position);
        return sum + valueCents / 100;
      }, 0),
    [sortedPositions],
  );

  const latestVisibleTotalPnlRef = useRef(visibleTotalPnl);
  const previousSportFilterRef = useRef(sportFilterKey);
  const didPrimePnlCardRef = useRef(sortedPositions.length > 0);
  const [displayTotalPnl, setDisplayTotalPnl] = useState(visibleTotalPnl);
  const [pnlCardAnchorMs, setPnlCardAnchorMs] = useState(() => Date.now());

  useEffect(() => {
    latestVisibleTotalPnlRef.current = visibleTotalPnl;
  }, [visibleTotalPnl]);

  useEffect(() => {
    if (didPrimePnlCardRef.current || sortedPositions.length === 0) return;
    didPrimePnlCardRef.current = true;
    setDisplayTotalPnl(visibleTotalPnl);
    setPnlCardAnchorMs(Date.now());
  }, [sortedPositions.length, visibleTotalPnl]);

  useEffect(() => {
    if (previousSportFilterRef.current === sportFilterKey) return;
    previousSportFilterRef.current = sportFilterKey;
    latestVisibleTotalPnlRef.current = visibleTotalPnl;
    setDisplayTotalPnl(visibleTotalPnl);
    setPnlCardAnchorMs(Date.now());
  }, [sportFilterKey, visibleTotalPnl]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
      setPnlCardAnchorMs(Date.now());
    }, PNL_CARD_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  // Card-view VMs — the same sorted rows the tables consume, projected into
  // the card layout. Built here because TraderView owns the market-store
  // accessors and the queue metrics.
  const orderCardVMs = useMemo<OrderCardVM[]>(() => {
    void marketVersion;
    return sortedOrders.map((row) => {
      if (row.kind === 'poly') return buildPolyOrderVM(row.order);
      const metrics = queueMetricsByOrderId.get(row.order.orderId);
      const eventStartMs = Number.isFinite(row.sortStart) ? row.sortStart : null;
      return buildKalshiOrderVM(row.order, {
        lastTradeYesCents: getLastTradeYesCents(row.order.ticker),
        book: getOrderbookRaw(row.order.ticker),
        queue: metrics?.queue ?? null,
        queueAbove: metrics?.queueAbove ?? null,
        queueBehind: metrics?.queueBehind ?? null,
        queueTotal: metrics?.total ?? null,
        filledQty: recentFilledQtyByOrderId.get(row.order.orderId) ?? 0,
        feeAdjustedOdds,
        makerRebatePct,
        eventStartMs,
        eventStartLabel: formatStartTimeLocal(eventStartMs),
      });
    });
  }, [
    sortedOrders,
    queueMetricsByOrderId,
    getLastTradeYesCents,
    getOrderbookRaw,
    feeAdjustedOdds,
    makerRebatePct,
    feesReady,
    marketVersion,
    recentFilledQtyByOrderId,
  ]);

  const positionCardVMs = useMemo<PositionCardVM[]>(
    () =>
      sortedPositions.map((row) => {
        const vm =
          row.kind === 'poly'
            ? buildPolyPositionVM(
                row.pos,
                formatStartTimeLocal(Number.isFinite(row.sortStart) ? row.sortStart : null),
              )
            : buildKalshiPositionVM(
                row.pos,
                row.pendingSellQty,
                formatStartTimeLocal(Number.isFinite(row.sortStart) ? row.sortStart : null),
              );
        // Volume on the tiles (Joshua's Jun 8 call notes). Snapshot at list
        // build — volume moves slowly; not worth per-frame VM churn.
        return {
          ...vm,
          volume: useMarketStore.getState().getMarket(vm.ticker)?.volume ?? null,
        };
      }),
    [sortedPositions],
  );

  // Tallies for the order card-view summary strip.
  const orderSummary = useMemo(() => {
    let atFront = 0;
    let queueFront = 0;
    let inSale = 0;
    let behind = 0;
    let wayBehind = 0;
    for (const vm of orderCardVMs) {
      if (vm.activeInSale) inSale += 1;
      if (vm.bookDiff === 0) atFront += 1;
      else if (vm.bookDiff != null && vm.bookDiff > 0) {
        if (
          vm.queueAbove != null
          && vm.queueAbove >= WAY_BEHIND_QUEUE_ABOVE_THRESHOLD
        ) {
          wayBehind += 1;
        } else {
          behind += 1;
        }
      }
      if (vm.queue === 0) queueFront += 1;
    }
    return {
      total: orderCardVMs.length,
      atFront,
      queueFront,
      inSale,
      behind,
      wayBehind,
    };
  }, [orderCardVMs]);

  // Feed the open-order velocity tracker — one sample per poll cycle.
  useEffect(() => {
    for (const vm of orderCardVMs) {
      recordOrderSample(vm.key, vm.queue, vm.filledQty);
    }
    pruneOrderVelocity(new Set(orderCardVMs.map((vm) => vm.key)));
  }, [orderCardVMs]);

  // Feed the per-ticker price velocity tracker. Sample once per effect-tick
  // for every distinct Kalshi ticker currently visible across open orders
  // and open positions. marketVersion is in the dep array so a fresh hydra
  // tick re-runs this effect and the 1m / 5m chips stay live as the market
  // moves. Polymarket isn't sampled — marketStore doesn't surface poly
  // current price on the position-card VM yet, so there's no source.
  useEffect(() => {
    const tickers = new Set<string>();
    for (const vm of orderCardVMs) {
      if (vm.venue === 'kalshi') tickers.add(vm.ticker);
    }
    for (const vm of positionCardVMs) {
      if (vm.venue === 'kalshi') tickers.add(vm.ticker);
    }
    for (const ticker of tickers) {
      const yesPrice = getMarketPrice(ticker);
      if (yesPrice != null) recordPriceSample(ticker, yesPrice);
    }
    prunePriceVelocity(tickers);
  }, [orderCardVMs, positionCardVMs, marketVersion, getMarketPrice]);

  // ── Cross-venue divergence (Poly vs Kalshi) ────────────────────────────────
  // Dan's ask: warn when Polymarket's mid pulls >= 2¢ from Kalshi on a market
  // you're in. Keyed by Kalshi ticker; the cards/rows look up their own ticker.
  const heldKalshiTickers = useMemo(() => {
    const s = new Set<string>();
    for (const vm of orderCardVMs) if (vm.venue === 'kalshi') s.add(vm.ticker);
    for (const vm of positionCardVMs) if (vm.venue === 'kalshi') s.add(vm.ticker);
    return s;
  }, [orderCardVMs, positionCardVMs]);
  // Stable identity for the held set (the Set above gets a fresh reference every
  // market tick) — lets the divergence sweep skip recompute on frames where the
  // holdings didn't actually change. Mirrors orderTickersKey above.
  const heldKalshiKey = useMemo(
    () => [...heldKalshiTickers].sort().join('|'),
    [heldKalshiTickers],
  );

  // Coarse clock for the divergence recompute (see DIVERGENCE_REFRESH_MS). The
  // sweep keys on this tick + the held set, NOT marketVersion, so it runs ~1/s
  // instead of ~30/s. A holdings change re-keys immediately via heldKalshiKey.
  const [divergenceTick, setDivergenceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setDivergenceTick((t) => t + 1),
      DIVERGENCE_REFRESH_MS,
    );
    return () => window.clearInterval(id);
  }, []);

  // Build ticker -> divergence. getGames already pairs Kalshi<->Poly per game
  // side via canonical key; we only sweep the sports the held tickers touch (not
  // all 14) and only keep entries for markets the trader actually holds. Poly
  // freshness is decided inside computeDivergence (getOrderbookAgeMs), so a
  // stale poly feed yields a !fresh entry that never alerts. (Poly market data
  // DOES reach both lanes over the hydra WS — the Polymarket geoblock is
  // order-POST-only — so this is live on g1.pm staging too whenever the poly
  // feed is up; it's UI/sound-only, so harmless there.)
  const divergenceByTicker = useMemo(() => {
    const map = new Map<string, DivergenceInfo>();
    if (heldKalshiTickers.size === 0) return map;
    const sports = new Set<Sport>();
    for (const t of heldKalshiTickers) {
      const sp = sportFromTicker(t);
      if (sp) sports.add(sp);
    }
    for (const sp of sports) {
      const games = getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
      for (const g of games) {
        if (!g.kalshi || !g.polymarket) continue;
        for (const side of ['away', 'home'] as const) {
          const kBook = g.kalshi[side];
          const kTicker = kBook?.marketTicker;
          if (!kTicker || !heldKalshiTickers.has(kTicker)) continue;
          const pBook = g.polymarket[side];
          const polyTicker = pBook?.marketTicker ?? '';
          const polyAgeMs = polyTicker ? getOrderbookAgeMs(polyTicker) : null;
          const info = computeDivergence(kBook, pBook, polyTicker, polyAgeMs);
          if (info) map.set(kTicker, info);
        }
      }
    }
    return map;
    // heldKalshiTickers is captured via the stable heldKalshiKey; divergenceTick
    // is the coarse refresh clock. (exhaustive-deps would re-add marketVersion.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKalshiKey, divergenceTick, getGames, getOrderbookAgeMs]);

  // ── U/P odds (Unabated consensus / Polymarket mid) per position ───────────
  // Feeds the positions table's U/P column and the position card's U/P stat
  // (replaced the positions 1m Δ chip). Keyed by the row/VM key
  // (`${ticker}-${side}` kalshi, `poly-pos-${ticker}-${side}` poly), values
  // already side-native. Moneyline-only by construction — the sweep walks
  // getGames moneyline rows, so spread/total/period positions get no entry
  // and render '—'.
  const heldPositionRefs = useMemo(() => {
    const kalshi = new Map<string, { key: string; side: 'yes' | 'no' }[]>();
    const poly = new Map<string, { key: string; side: 'yes' | 'no' }[]>();
    const sports = new Set<Sport>();
    for (const row of baseSortedPositions) {
      if (row.sport) sports.add(row.sport);
      if (row.kind === 'kalshi') {
        const list = kalshi.get(row.pos.ticker) ?? [];
        list.push({ key: `${row.pos.ticker}-${row.pos.side}`, side: row.pos.side });
        kalshi.set(row.pos.ticker, list);
      } else {
        const list = poly.get(row.pos.ticker) ?? [];
        list.push({ key: `poly-pos-${row.pos.ticker}-${row.pos.side}`, side: row.pos.side });
        poly.set(row.pos.ticker, list);
      }
    }
    return { kalshi, poly, sports };
  }, [baseSortedPositions]);
  // Stable identity for the held-position set (baseSortedPositions gets a fresh
  // reference every market tick via sortStartMs) — mirrors heldKalshiKey.
  const heldPositionsKey = useMemo(() => {
    const keys: string[] = [];
    for (const list of heldPositionRefs.kalshi.values()) for (const h of list) keys.push(h.key);
    for (const list of heldPositionRefs.poly.values()) for (const h of list) keys.push(h.key);
    return keys.sort().join('|');
  }, [heldPositionRefs]);

  const upOddsByKey = useMemo(() => {
    const map = new Map<string, UPOdds>();
    const { kalshi: heldK, poly: heldP, sports } = heldPositionRefs;
    if (heldK.size === 0 && heldP.size === 0) return map;
    const store = useMarketStore.getState();
    for (const sp of sports) {
      const games = getGames(sp, 'moneyline', { dateFilter: 'all', statusFilter: 'all' });
      for (const g of games) {
        const kAway = g.kalshi?.away.marketTicker;
        const kHome = g.kalshi?.home.marketTicker;
        const pAway = g.polymarket?.away.marketTicker;
        const pHome = g.polymarket?.home.marketTicker;
        const touchesHeld =
          (kAway != null && heldK.has(kAway)) ||
          (kHome != null && heldK.has(kHome)) ||
          (pAway != null && heldP.has(pAway)) ||
          (pHome != null && heldP.has(pHome));
        if (!touchesHeld) continue;
        // Full-game result consensus. The row key IS the consensus cache key
        // (doubleheader-safe v2); strip synth suffixes defensively, mirroring
        // GameTable. consensus.awayTeam can land in the canonical key's
        // homeCode slot — swap when it does (same rule as GameTable).
        const consensus = store.getConsensusFor(
          g.key.split('|multi|')[0].replace(/\|advance$/, ''),
        );
        const swapped = consensus != null && consensus.awayTeam === g.homeCode;
        const uYesFor = (teamSide: TeamSide): number | null => {
          if (!consensus) return null;
          // Tenths when hydra sends them (the U-row precedent in the order
          // panel); integer cents on pre-rollout hydra.
          const away =
            consensus.awayFairProbTenths != null
              ? consensus.awayFairProbTenths / 10
              : consensus.awayFairProbCents;
          const home =
            consensus.homeFairProbTenths != null
              ? consensus.homeFairProbTenths / 10
              : consensus.homeFairProbCents;
          return (teamSide === 'away') !== swapped ? away : home;
        };
        for (const teamSide of ['away', 'home'] as const) {
          const pBook = g.polymarket?.[teamSide];
          const polyMid = bookMid(pBook);
          const polyAgeMs = pBook ? getOrderbookAgeMs(pBook.marketTicker) : null;
          const polyFresh = polyAgeMs != null && polyAgeMs <= POLY_STALE_MS;
          const uYes = uYesFor(teamSide);
          // Kalshi holdings: exact ticker match on this team slot.
          const kTicker = g.kalshi?.[teamSide]?.marketTicker;
          for (const h of (kTicker != null && heldK.get(kTicker)) || []) {
            map.set(h.key, {
              uCents: sideNativeProbCents(uYes, h.side),
              pCents: sideNativeProbCents(polyMid, h.side),
              polyFresh,
            });
          }
          // Poly holdings: the same slug ticker sits on BOTH team slots, so a
          // ticker match alone arms the opposite team ~half the time — resolve
          // the held token against this slot's yes/no token ids instead (same
          // rule as resolveBuyPanelTarget).
          if (pBook) {
            for (const h of heldP.get(pBook.marketTicker) ?? []) {
              const mkt = store.getMarket(pBook.marketTicker);
              if (!mkt) continue;
              const heldToken = h.side === 'yes' ? mkt.yesTokenId : mkt.noTokenId;
              if (!heldToken) continue;
              const effSide =
                pBook.tokenId === heldToken
                  ? ('yes' as const)
                  : pBook.noTokenId === heldToken
                    ? ('no' as const)
                    : null;
              if (!effSide) continue;
              map.set(h.key, {
                uCents: sideNativeProbCents(uYes, effSide),
                pCents: sideNativeProbCents(polyMid, effSide),
                polyFresh,
              });
            }
          }
        }
      }
    }
    return map;
    // heldPositionRefs is captured via the stable heldPositionsKey;
    // divergenceTick is the coarse 1s refresh clock (consensus + a cross-venue
    // mid don't need the ~30Hz market tick — same rationale as divergence).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldPositionsKey, divergenceTick, getGames, getOrderbookAgeMs]);

  const [divergenceFlashTickers, setDivergenceFlashTickers] = useState<Set<string>>(
    () => new Set(),
  );
  const divergenceFlashTimersRef = useRef<Map<string, number>>(new Map());
  const triggerDivergenceFlash = useCallback((tickers: string[]) => {
    if (tickers.length === 0) return;
    setDivergenceFlashTickers((prev) => {
      const next = new Set(prev);
      for (const t of tickers) next.add(t);
      return next;
    });
    for (const t of tickers) {
      const existing = divergenceFlashTimersRef.current.get(t);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setDivergenceFlashTickers((prev) => {
          if (!prev.has(t)) return prev;
          const next = new Set(prev);
          next.delete(t);
          return next;
        });
        divergenceFlashTimersRef.current.delete(t);
      }, DIVERGENCE_FLASH_MS);
      divergenceFlashTimersRef.current.set(t, timer);
    }
  }, []);
  useEffect(
    () => () => {
      for (const timer of divergenceFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      divergenceFlashTimersRef.current.clear();
    },
    [],
  );

  // Edge-detect crossings into the alert band. The latch rules (silent
  // first-sight seed, ring once on false→true, fresh-gated re-arm with
  // hysteresis, prune-by-holdings) live in the unit-tested pure helper
  // stepDivergenceLatch — see its tests for the arm/re-arm/stale/one-sided-book
  // cases. Here we just persist the latch across ticks and fan the fired set out
  // to the flash + sound.
  const divergenceArmedRef = useRef<Map<string, boolean>>(new Map());
  useEffect(() => {
    const { nextArmed, fired } = stepDivergenceLatch(
      divergenceArmedRef.current,
      divergenceByTicker,
      heldKalshiTickers,
    );
    divergenceArmedRef.current = nextArmed;
    if (fired.length > 0) {
      // Visual flash always; the CHIME is additionally gated on the live-game
      // preference — in-play books legitimately diverge, so by default a game
      // that has started flashes silently (divergenceRingTickers).
      triggerDivergenceFlash(fired);
      const soundPrefs = readSoundSettings();
      if (soundPrefs.divergenceSoundEnabled) {
        const ringable = divergenceRingTickers(
          fired,
          soundPrefs.divergenceLiveGameSoundEnabled,
          (t) => useMarketStore.getState().getEventStartTimeMsByTicker(t),
          Date.now(),
        );
        if (ringable.length > 0) playDivergenceTone();
      }
    }
    // heldKalshiTickers is read via the divergenceByTicker render (same set that
    // built the map); adding it to deps would just re-run on every market tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [divergenceByTicker, triggerDivergenceFlash]);

  const [queueCriticalFlashIds, setQueueCriticalFlashIds] = useState<Set<string>>(
    () => new Set(),
  );
  const queueCriticalFlashTimersRef = useRef<Map<string, number>>(new Map());

  const triggerQueueCriticalFlash = useCallback((orderIds: string[]) => {
    if (orderIds.length === 0) return;

    setQueueCriticalFlashIds((prev) => {
      const next = new Set(prev);
      for (const orderId of orderIds) next.add(orderId);
      return next;
    });

    for (const orderId of orderIds) {
      const existing = queueCriticalFlashTimersRef.current.get(orderId);
      if (existing != null) window.clearTimeout(existing);
      const timer = window.setTimeout(() => {
        setQueueCriticalFlashIds((prev) => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        queueCriticalFlashTimersRef.current.delete(orderId);
      }, QUEUE_CRITICAL_FLASH_MS);
      queueCriticalFlashTimersRef.current.set(orderId, timer);
    }
  }, []);

  useEffect(() => {
    return () => {
      for (const timer of queueCriticalFlashTimersRef.current.values()) {
        window.clearTimeout(timer);
      }
      queueCriticalFlashTimersRef.current.clear();
    };
  }, []);

  // Fire one-shot alerts when an order's cumulative queue position crosses a
  // threshold from above. Tracking the previously-seen value per orderId means
  // a freshly observed order already deep in the queue (e.g. on page load)
  // doesn't spuriously alert — only an actual downward crossing does. Critical
  // crossings also flash the card briefly so the trader can see which order
  // triggered the sub-10k sound.
  const prevQueueRef = useRef<Map<string, number>>(new Map());
  const prevQueueAboveRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const prev = prevQueueRef.current;
    const prevAbove = prevQueueAboveRef.current;
    const next = new Map<string, number>();
    const nextAbove = new Map<string, number>();
    const criticalOrderIds: string[] = [];
    let warn = false;
    let crit = false;
    let fellBehind = false;
    for (const [orderId, metrics] of queueMetricsByOrderId) {
      const q = metrics.queue;
      if (q != null) {
        next.set(orderId, q);
        const last = prev.get(orderId);
        if (last != null) {
          if (last > QUEUE_CRITICAL_THRESHOLD && q <= QUEUE_CRITICAL_THRESHOLD) {
            crit = true;
            criticalOrderIds.push(orderId);
          } else if (last > QUEUE_WARNING_THRESHOLD && q <= QUEUE_WARNING_THRESHOLD) {
            warn = true;
          }
        }
      }
      // StreamRift #5: alert when a new level forms ABOVE this order. queueAbove
      // (better-priced contracts that clear before your level) crossing UP through
      // the way-behind threshold means a big level just formed ahead and you've
      // fallen behind. Same one-shot discipline as the queue alert above: only an
      // actual upward crossing fires, and a freshly-observed order (no prior
      // sample) never alerts.
      const above = metrics.queueAbove;
      if (above != null) {
        nextAbove.set(orderId, above);
        const lastAbove = prevAbove.get(orderId);
        if (
          lastAbove != null &&
          lastAbove < WAY_BEHIND_QUEUE_ABOVE_THRESHOLD &&
          above >= WAY_BEHIND_QUEUE_ABOVE_THRESHOLD
        ) {
          fellBehind = true;
        }
      }
    }
    prevQueueRef.current = next;
    prevQueueAboveRef.current = nextAbove;
    triggerQueueCriticalFlash(criticalOrderIds);
    if (crit || warn || fellBehind) {
      const settings = readSoundSettings();
      if (settings.queueFrontSoundEnabled) {
        // Advancing-toward-fill urgency wins; falling-behind is the gentlest cue.
        if (crit) playQueueCriticalTone();
        else if (warn) playQueueWarningTone();
        else if (fellBehind) playFellBehindTone();
      }
    }
  }, [queueMetricsByOrderId, triggerQueueCriticalFlash]);

  const [editPosition, setEditPosition] = useState<EditPositionData | null>(null);
  const [cancelingOrder, setCancelingOrder] = useState<string | null>(null);
  const [cancelingPolyOrder, setCancelingPolyOrder] = useState<string | null>(null);
  const [pendingCancelOrder, setPendingCancelOrder] = useState<PendingCancelOrder | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Edit-price modal target. One modal for both venues; venue-specific replace
  // logic dispatches via handleReplaceFromModal below.
  const [editOrder, setEditOrder] = useState<EditOrderData | null>(null);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      setCancelingOrder(orderId);
      try {
        await cancelOrder(orderId);
      } catch (err) {
        console.error('Cancel order failed:', err);
        throw err instanceof Error ? err : new Error('Cancel order failed');
      } finally {
        setCancelingOrder(null);
      }
    },
    [cancelOrder],
  );

  const handleCancelPolyOrder = useCallback(
    async (orderId: string) => {
      setCancelingPolyOrder(orderId);
      try {
        const out = await polyOrdersState.cancel(orderId);
        if (!out.ok) {
          console.error('Cancel poly order failed:', out.error);
          throw new Error(out.error);
        }
      } finally {
        setCancelingPolyOrder(null);
      }
    },
    [polyOrdersState],
  );

  // In-page buy window (Joshua, Jun 11 standup: "click on a game [on the
  // position tracker], it brings up the buying window on the right").
  // Stores the resolved game's KEY, not the row — the live row is re-derived
  // from marketStore every render (below) so panel prices tick; the snapshot
  // taken at click time is the fallback if the row leaves the store while
  // the panel is open.
  const [buyPanel, setBuyPanel] = useState<{
    gameKey: string;
    snapshot: GameRow;
    intent: TradeIntent;
  } | null>(null);
  // Page-level YES/NO context for the panel's pills — seeded from the
  // clicked position's held side, flipped by clicks inside the panel.
  const [panelMarketSide, setPanelMarketSide] = useState<'yes' | 'no'>('no');

  // Dash-wide click contract (Dan, Jul 2026): clicking a game/event row or
  // card opens the ORDER SCREEN (in-page buy panel on the right); the
  // GameDetailLink icon beside the market label is the separate, explicit
  // path to the /game/... detail page. The navigate fallback here covers
  // games the live store can't resolve (settled — their books are dead, so
  // no order panel could open): the detail page is the only useful surface
  // left for that click.
  const openOrderPanel = useCallback(
    (ticker: string, side: 'yes' | 'no' | null | undefined) => {
      if (!side) return;
      const target = resolveBuyPanelTarget(ticker, side);
      if (target) {
        setBuyPanel({ gameKey: target.game.key, snapshot: target.game, intent: target.intent });
        setPanelMarketSide(side);
        return;
      }
      const href = gameDetailHref(ticker, side);
      if (href) navigate(href);
    },
    [navigate],
  );

  // Live row for the open panel — same re-derive-per-render pattern the
  // dashboard uses for expandedGame, so prices/books tick with the feed.
  // feedVersion IS the page's throttled 4 Hz clock (was a second raw-counter
  // subscription); the buy panel's one-sport getGames sweep runs ≤4×/s.
  const feedVersion = marketVersion;
  const buyPanelGame = useMemo(() => {
    if (!buyPanel) return null;
    void feedVersion;
    // One sport only — this runs on every RAF-coalesced feed tick while
    // the panel is open, and a full all-sports getGames sweep is ~10ms+
    // against a session-grown _markets map (review catch). Key match is
    // normalized because per-day row keys flip shape when hydra tags
    // canonicalGameKey late — exact-match would strand the panel on the
    // click-time snapshot forever.
    const want = normalizeGameKey(buyPanel.gameKey);
    const hit = useMarketStore
      .getState()
      .getGames(buyPanel.snapshot.sport, 'moneyline', { dateFilter: 'all', statusFilter: 'all' })
      .find((g) => normalizeGameKey(g.key) === want);
    return hit ?? buyPanel.snapshot;
  }, [buyPanel, feedVersion]);

  const executeCancelOrder = useCallback(
    async (target: PendingCancelOrder) => {
      setCancelError(null);
      try {
        if (target.venue === 'kalshi') {
          await handleCancelOrder(target.orderId);
        } else {
          await handleCancelPolyOrder(target.orderId);
        }
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Cancel failed. Check open orders and try again.';
        setCancelError(message);
      }
    },
    [handleCancelOrder, handleCancelPolyOrder],
  );

  // End-of-day sweep (Kevin, Jun 2: "Cancel all open orders button for when
  // we sign off for the day"). Sweeps the UNFILTERED order lists — sport
  // filter chips must never silently shrink an "ALL" sweep (orders left
  // resting overnight). Per-order endpoints on both venues, so a trader on
  // shared org keys can never nuke teammates' orders (the relay's poly
  // DELETE-all is org-credential-wide; deliberately not used here). Raw
  // calls — NOT handleCancelOrder/accountStore.cancelOrder — so each cancel
  // doesn't fan out its own refresh (2N+1 request storm invites venue 429s
  // mid-sweep); one refresh per venue at the end. Always confirms,
  // regardless of the per-order confirmOrderCancel preference.
  const [cancelAllOpen, setCancelAllOpen] = useState(false);
  const [cancelingAll, setCancelingAll] = useState(false);
  const cancelAllTargetCount =
    openOrders.length + (polyOrdersState.unavailableCode ? 0 : polyOrdersState.orders.length);
  const handleCancelAllOrders = useCallback(async () => {
    setCancelError(null);
    setCancelingAll(true);
    let failed = 0;
    try {
      for (const order of openOrders) {
        try {
          const res = await apiFetch(`/api/kalshi/orders/${order.orderId}`, {
            method: 'DELETE',
          });
          if (!res.ok) failed += 1;
        } catch {
          failed += 1;
        }
      }
      if (!polyOrdersState.unavailableCode) {
        for (const order of polyOrdersState.orders) {
          const id = String(order.id ?? order.orderID ?? order.orderId ?? '');
          if (!id) {
            failed += 1;
            continue;
          }
          try {
            const out = await cancelPolyOrderViaRelay(id);
            if (!out.ok) failed += 1;
          } catch {
            failed += 1;
          }
        }
      }
    } finally {
      setCancelingAll(false);
      void refreshOrders();
      void refreshPolyOrders();
    }
    if (failed > 0) {
      setCancelError(
        `Cancel all: ${failed} order${failed === 1 ? '' : 's'} failed to cancel (may have just filled or already been canceled) — check Open Orders and retry.`,
      );
    }
  }, [openOrders, polyOrdersState.orders, polyOrdersState.unavailableCode, refreshOrders, refreshPolyOrders]);

  const requestCancelOrder = useCallback(
    (target: PendingCancelOrder) => {
      setCancelError(null);
      if (confirmOrderCancel) {
        setPendingCancelOrder(target);
        return;
      }
      void executeCancelOrder(target);
    },
    [confirmOrderCancel, executeCancelOrder],
  );

  // Modal dispatch — single onReplace callback that the EditOrderPriceModal
  // calls when the trader hits "Replace order". Picks the right replace
  // implementation based on the order's venue + passes the new qty through.
  // Kalshi's accountStore.replaceOrder smart-routes by default (same-price
  // qty-shrink through /decrease for queue preservation); the modal can
  // opt out via forceCancelReplace when the trader explicitly wants to
  // re-queue. Polymarket is always cancel + place (the forceCancelReplace
  // flag is meaningless there since there's no smart route to override —
  // dropped silently). Throws on failure so the modal surfaces the error
  // inline; resolves on success so the modal closes via setEditOrder(null).
  const handleReplaceFromModal = useCallback(
    async (
      newPriceCents: number,
      newQuantity: number,
      options?: { forceCancelReplace?: boolean },
    ) => {
      if (!editOrder) throw new Error('No order selected');
      if (editOrder.venue === 'kalshi') {
        await replaceOrder(
          editOrder.orderId,
          newPriceCents,
          newQuantity,
          options?.forceCancelReplace,
        );
      } else {
        const out = await polyOrdersState.replace(
          editOrder.orderId,
          newPriceCents,
          {
            tickSize: editOrder.tickSize,
            negRisk: editOrder.negRisk,
            nextQuantity: newQuantity,
          },
        );
        if (!out.ok) throw new Error(out.error);
      }
      setEditOrder(null);
    },
    [editOrder, replaceOrder, polyOrdersState],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        refreshPositions(),
        refreshOrders(),
        refreshPnl(),
        refreshPolyOrders(),
        refreshPolyPositions(),
      ]);
      setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
      setPnlCardAnchorMs(Date.now());
    } finally {
      setRefreshing(false);
    }
  }, [
    refreshPositions,
    refreshOrders,
    refreshPnl,
    refreshPolyOrders,
    refreshPolyPositions,
  ]);

  if (status !== 'connected') {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="bg-card border-border text-muted-foreground flex flex-col items-center justify-center gap-3 rounded-lg border px-4 py-8 text-center">
          <Crosshair size={48} strokeWidth={1} />
          <h2 className="text-foreground text-lg font-medium">Position Tracker</h2>
          <p className="text-sm">Connect your Kalshi account in Settings to track positions</p>
        </div>
        <div className="mt-6">
          <BotZones />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="flex gap-6">
        <div className="min-w-0 flex-1 space-y-5">
          {/* Sticky toolbar only (cards scroll). -top-6 matches the -mt-6 so it
              pins flush to the top instead of leaving a gap below the container's
              top padding when scrolled. */}
          <div className="border-border/70 bg-background/95 supports-[backdrop-filter]:bg-background/85 sticky -top-6 z-30 -mx-6 -mt-6 border-b px-6 pb-3 pt-6 backdrop-blur">
            {/* Header */}
          <div className="flex items-center justify-between gap-3">
            {/* Left: title + balance (balance reads better here than far right) */}
            <div className="flex shrink-0 items-center gap-3">
              <Crosshair size={20} className="text-muted-foreground" />
              <h1 className="text-xl font-semibold">Positions</h1>
              {balance !== null ? (
                <span className="text-muted-foreground text-sm">
                  Balance:{' '}
                  <span className="text-foreground font-medium">
                    ${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </span>
              ) : keyMode === 'org' ? (
                <span className="text-muted-foreground text-xs">Balance tracked at org level</span>
              ) : null}
              <OrderbooksPausedBadge />
            </div>
            {/* Middle: sport chips on a single row — scroll on overflow, never wrap */}
            <div
              className="min-w-0 flex-1 overflow-x-auto"
              role="group"
              aria-label="Filter positions and orders by sport. Click a sport to include it, again to exclude it (red), a third time to clear."
            >
              <div className="flex w-max items-center gap-1">
                <SportFilterChip
                  mode={sportFilter.size === 0 ? 'include' : 'off'}
                  label="All sports"
                  onClick={handleAllSportsFilter}
                />
                {SPORT_TABS.map((sport) => (
                  <SportFilterChip
                    key={sport.id}
                    mode={sportFilter.get(sport.id) ?? 'off'}
                    label={sport.label}
                    onClick={() => handleSportFilterToggle(sport.id)}
                    cycleHint
                  />
                ))}
              </div>
            </div>
            {/* Right: refresh + Tracker toggle — by the rail it controls */}
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1.5 transition-colors"
              >
                <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button
                type="button"
                onClick={toggleTrackerHidden}
                aria-pressed={!trackerHidden}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  !trackerHidden
                    ? 'border-primary/50 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={
                  trackerHidden
                    ? 'Show the position tracker in the rail'
                    : trackerPopped
                      ? 'Tracker is in its own window — toggle to hide it'
                      : 'Hide the position tracker'
                }
              >
                <Crosshair size={14} />
                <span>Tracker</span>
              </button>
              <button
                type="button"
                onClick={toggleFillsHidden}
                aria-pressed={!fillsHidden}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  !fillsHidden
                    ? 'border-primary/50 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                title={
                  fillsHidden
                    ? 'Show recent fills in the rail'
                    : fillsPopped
                      ? 'Recent fills are in their own window — toggle to hide them'
                      : 'Hide recent fills'
                }
              >
                <Receipt size={14} />
                <span>Fills</span>
              </button>
            </div>
          </div>
          </div>

          {/* Summary Cards — scroll normally (no longer pinned, so they can't
              ride over the Open Positions view-mode buttons). */}
          <div className={`grid gap-3 ${keyMode === 'org' ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="text-muted-foreground text-xs">Unrealized P&L</div>
                  {/* P&L visibility toggle — hidden by default (Joshua, Jun 2:
                      "sucks to see a negative"); persisted in displaySettings.
                      Color class is also withheld while hidden so the green/red
                      tint can't leak the sign. */}
                  <button
                    onClick={toggleShowPnl}
                    className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
                    title={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                    aria-label={showPnl ? 'Hide P&L dollars' : 'Show P&L dollars'}
                    aria-pressed={showPnl}
                  >
                    {showPnl ? <Eye size={13} /> : <EyeOff size={13} />}
                  </button>
                </div>
                <RefreshProgress
                  anchorMs={pnlCardAnchorMs}
                  intervalMs={PNL_CARD_REFRESH_INTERVAL_MS}
                  refreshing={refreshing}
                />
              </div>
              <div
                className={`mt-1 text-lg font-semibold ${
                  !showPnl
                    ? 'text-muted-foreground'
                    : displayTotalPnl >= 0
                      ? 'text-positive'
                      : 'text-negative'
                }`}
                title={showPnl ? undefined : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'}
              >
                {showPnl ? formatDollars(displayTotalPnl) : '•••'}
              </div>
            </div>
            {keyMode === 'org' && (
              <div className="bg-card border-border rounded-lg border px-4 py-3">
                <div className="text-muted-foreground text-xs">Realized P&L</div>
                <div
                  className={`mt-1 text-lg font-semibold ${
                    !showPnl
                      ? 'text-muted-foreground'
                      : realizedPnlCents >= 0
                        ? 'text-positive'
                        : 'text-negative'
                  }`}
                  title={showPnl ? undefined : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'}
                >
                  {showPnl ? formatDollars(realizedPnlCents) : '•••'}
                </div>
              </div>
            )}
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs">Positions</div>
              <div className="text-foreground mt-1 text-lg font-semibold">
                {sortedPositions.length}
                <span className="text-muted-foreground ml-1 text-sm font-normal">
                  / {fmtUsd(visiblePositionValueDollars)}
                </span>
              </div>
            </div>
            <div className="bg-card border-border rounded-lg border px-4 py-3">
              <div className="text-muted-foreground text-xs">Open Orders</div>
              <div className="text-foreground mt-1 text-lg font-semibold">
                {sortedOrders.length}
              </div>
            </div>
          </div>

          {/* Open Positions — header is constant page chrome; only the
              item shape (compact list vs rows vs cards) changes. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h2 className="text-foreground text-sm font-semibold">Open Positions</h2>
                <InfoTooltip title="Open Positions">
                  <PositionsHelp />
                </InfoTooltip>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {positionViewMode !== 'table' && sortedPositions.length > 0 && (
                  <div className="border-border inline-flex overflow-hidden rounded-md border">
                    <SortSegmentButton
                      active={positionSort.key === 'default'}
                      dir={positionSort.dir}
                      label="Default"
                      title="Sort positions by event time, then ticker"
                      onClick={() => handlePositionSort('default')}
                    />
                    <SortSegmentButton
                      active={positionSort.key === 'size'}
                      dir={positionSort.dir}
                      label="Size"
                      title="Sort positions by contract size"
                      onClick={() => handlePositionSort('size')}
                    />
                    <SortSegmentButton
                      active={positionSort.key === 'velocity'}
                      dir={positionSort.dir}
                      label="Velocity"
                      title="Sort positions by how fast their Kalshi market price is moving (¢/min)"
                      onClick={() => handlePositionSort('velocity')}
                    />
                  </div>
                )}
                <ViewModeToggle
                  value={positionViewMode}
                  modes={POSITION_VIEW_MODES}
                  onChange={setPositionViewMode}
                />
              </div>
            </div>
            {sortedPositions.length > 0 ? (
              positionViewMode === 'card' || positionViewMode === 'mini' ? (
                <CardGrid variant={positionViewMode === 'mini' ? 'mini' : 'default'}>
                  {positionCardVMs.map((vm) => {
                    const href = gameDetailHref(vm.ticker, vm.side);
                    return (
                      <PositionCard
                        key={vm.key}
                        vm={vm}
                        mini={positionViewMode === 'mini'}
                        hidePnl={!showPnl}
                        upOdds={upOddsByKey.get(vm.key) ?? null}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={divergenceFlashTickers.has(vm.ticker)}
                        detailHref={href}
                        onOpen={href ? () => openOrderPanel(vm.ticker, vm.side) : undefined}
                        onEdit={() =>
                          setEditPosition({
                            ticker: vm.ticker,
                            side: vm.side,
                            position: vm.qty,
                            pendingSellQty: vm.pendingSellQty,
                            averagePrice: vm.avgCents,
                            currentPriceCents: vm.nowCents,
                          })
                        }
                      />
                    );
                  })}
                </CardGrid>
              ) : positionViewMode === 'compact' ? (
                <CompactListGrid>
                  {positionCardVMs.map((vm) => {
                    const href = gameDetailHref(vm.ticker, vm.side);
                    return (
                      <CompactPositionItem
                        key={vm.key}
                        vm={vm}
                        hidePnl={!showPnl}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={divergenceFlashTickers.has(vm.ticker)}
                        detailHref={href}
                        onOpen={href ? () => openOrderPanel(vm.ticker, vm.side) : undefined}
                        onEdit={() =>
                          setEditPosition({
                            ticker: vm.ticker,
                            side: vm.side,
                            position: vm.qty,
                            pendingSellQty: vm.pendingSellQty,
                            averagePrice: vm.avgCents,
                            currentPriceCents: vm.nowCents,
                          })
                        }
                      />
                    );
                  })}
                </CompactListGrid>
              ) : (
                  <div className="bg-card border-border rounded-lg border">
                    <table className="w-full">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                      <SortableTh
                        className="px-4 py-2 text-left"
                        sortKey="default"
                        activeSortKey={positionSort.key}
                        activeSortDir={positionSort.dir}
                        onSort={handlePositionSort}
                      >
                        Market
                      </SortableTh>
                      <th className="w-12 px-2 py-2 text-center">Source</th>
                      <th className="px-3 py-2 text-center">Side</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="size"
                        activeSortKey={positionSort.key}
                        activeSortDir={positionSort.dir}
                        onSort={handlePositionSort}
                        title="Sort by position size"
                      >
                        Qty
                      </SortableTh>
                      <th className="px-3 py-2 text-right">Avg</th>
                    <th className="px-3 py-2 text-right">Fees</th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Buy cost + fees"
                    >
                      Cost
                    </th>
                    <th className="px-3 py-2 text-right">Now</th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Unabated consensus / Polymarket mid for this market — both side-native (a NO position reads 100 − the YES-team value). '—' when the market isn't a paired moneyline or the source hasn't delivered."
                    >
                      U/P
                    </th>
                    {/* P&L + Cash Out are bracketed by left/right borders so the
                        eye reads them as a pair: "how that compares to entry" +
                        "what you'd take home now". Cash Out sits on the right
                        so the pencil-edit icon (embedded in that cell) lines
                        up at the row's far edge. */}
                    <th
                      className="border-border/40 border-l px-3 py-2 text-right"
                      title="If closing current contract (Cash Out − Cost)"
                    >
                      P&L
                    </th>
                    <th
                      className="border-border/40 border-r px-3 py-2 text-right"
                      title="Liquidation value: market value minus est. close fee — what actually hits your account on a market sell"
                    >
                      Cash Out
                    </th>
                    <th
                      className="px-3 py-2 text-right"
                      title="If holding to final settlement"
                    >
                      If W
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPositions.map((row) => {
                    if (row.kind === 'poly') {
                      // Polymarket position rows. Now / P&L / Cash Out / If W
                      // render — for poly: those need hydra mark prices and a
                      // Polymarket fee schedule we haven't wired in. Avg / Cost
                      // come straight from the persisted fills ledger via the
                      // relay positions endpoint.
                      const p = row.pos;
                      const label =
                        p.ticker.length > 16
                          ? `${p.ticker.slice(0, 6)}…${p.ticker.slice(-4)}`
                          : p.ticker;
                      const costCents = p.avgEntryPriceCents * p.netCount;
                      const href = gameDetailHref(p.ticker, p.side);
                      return (
                        <tr
                          key={`poly-pos-${p.ticker}-${p.side}`}
                          className={`border-border/40 hover:bg-muted/30 border-b ${
                            href ? 'cursor-pointer' : ''
                          }`}
                          title={p.ticker}
                          onClick={href ? () => openOrderPanel(p.ticker, p.side) : undefined}
                        >
                          <td className="text-foreground px-4 py-2 font-mono text-xs">
                            {label}
                            {href && <GameDetailLink href={href} className="-my-1 ml-1" />}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <VenueChip venue="polymarket" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                p.side === 'yes'
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-red-500/20 text-red-400'
                              }`}
                            >
                              {p.side.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {p.netCount}
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            {formatCents(p.avgEntryPriceCents)}
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            {p.feesPaidCents != null
                              ? fmtUsd(p.feesPaidCents / 100)
                              : '—'}
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {fmtUsd(costCents / 100)}
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          {/* U/P — resolved via the paired game slot's token
                              ids (the poly slug rides both team slots, so a
                              token match decides the side). Dash when the
                              relay ticker doesn't resolve in the live store. */}
                          <td className="px-3 py-2 text-right font-mono text-xs">
                            <UPOddsValue
                              odds={upOddsByKey.get(`poly-pos-${p.ticker}-${p.side}`) ?? null}
                            />
                          </td>
                          <td className="text-muted-foreground border-border/40 border-l px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          <td className="text-muted-foreground border-border/40 border-r px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                        </tr>
                      );
                    }
                    const pos = row.pos;
                    const pendingSellQty = row.pendingSellQty;
                    const { marketLabel, betTeam } = describeMarket(pos.ticker, pos.side);
                    const timeLabel = formatStartTimeLocal(row.sortStart);
                    const href = gameDetailHref(pos.ticker, pos.side);
                    const label = `${marketLabel}${timeLabel ? ` ${timeLabel}` : ''}`;
                    const divergence = divergenceByTicker.get(pos.ticker) ?? null;
                    return (
                    <tr
                      key={`${pos.ticker}-${pos.side}`}
                      className={`border-border/40 hover:bg-muted/30 border-b ${
                        href ? 'cursor-pointer' : ''
                      } ${divergenceFlashTickers.has(pos.ticker) ? 'animate-divergence-flash' : ''}`}
                      title={pos.ticker}
                      onClick={href ? () => openOrderPanel(pos.ticker, pos.side) : undefined}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        <span
                          className={href ? 'hover:text-blue-400 hover:underline' : undefined}
                        >
                          <BetTeamTitle title={label} team={betTeam} />
                        </span>
                        {href && <GameDetailLink href={href} className="-my-1 ml-1" />}
                        <DivergenceBadge info={divergence} className="ml-1.5 align-middle" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <VenueChip venue="kalshi" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            pos.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{pos.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {/* toFixed→Number: relay counts can be fractional
                            (fp fills) — show 12.5, never 3.3000000000000003 */}
                        {Number(pos.position.toFixed(2))}
                        {pendingSellQty > 0 && (
                          <span
                            className="text-amber-400 ml-1"
                            title={`${pendingSellQty} contract${pendingSellQty === 1 ? '' : 's'} on open sell order${pendingSellQty === 1 ? '' : 's'}`}
                          >
                            ({pendingSellQty})
                          </span>
                        )}
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        {formatCents(pos.averagePrice)}
                      </td>
                      <td
                        className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                        title={
                          pos.feesPaidCents == null
                            ? 'No fee data on this position\u2019s fills yet'
                            : undefined
                        }
                      >
                        {pos.feesPaidCents != null
                          ? fmtUsd(pos.feesPaidCents / 100)
                          : '—'}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {fmtUsd(pos.totalCostCents / 100)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${nowColorClass(pos.currentPrice)}`}
                      >
                        {pos.currentPrice !== null ? formatCents(pos.currentPrice) : '—'}
                      </td>
                      {/* U/P — Unabated consensus / Polymarket mid, side-native
                          (parity with the position card's U/P stat). Moneyline
                          markets only; other market types render '—'. */}
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        <UPOddsValue
                          odds={upOddsByKey.get(`${pos.ticker}-${pos.side}`) ?? null}
                        />
                      </td>
                      {/* P&L + Cash Out: bracketed pair. P&L is the round-trip
                          delta from cost basis; Cash Out is the post-fee
                          liquidation value. Pencil-edit lives in Cash Out so
                          the action sits at the row's far right edge. */}
                      <td
                        className="border-border/40 border-l px-3 py-2 text-right font-mono text-xs font-medium"
                        title="Cash Out − Cost (round-trip P&L if you sold now)"
                      >
                        <span
                          className={
                            !showPnl || pos.unrealizedPnl === null
                              ? 'text-muted-foreground'
                              : pos.unrealizedPnl >= 0
                                ? 'text-positive'
                                : 'text-negative'
                          }
                          title={showPnl ? undefined : 'P&L hidden — click the eye on the Unrealized P&L tile to reveal'}
                        >
                          {pos.unrealizedPnl === null
                            ? '—'
                            : showPnl
                              ? formatDollars(pos.unrealizedPnl)
                              : '•••'}
                        </span>
                      </td>
                      <td
                        className="text-foreground border-border/40 border-r px-3 py-2 text-right font-mono text-xs"
                        title={
                          pos.estClosingFeeCents !== null
                            ? `Market value ${fmtUsd((pos.totalValueCents ?? 0) / 100)} − est. close fee ${fmtUsd(pos.estClosingFeeCents / 100)}`
                            : 'Close fee not estimated (rate not loaded for this series) — value shown is pre-fee'
                        }
                      >
                        <div className="flex items-center justify-end gap-2">
                          <span>
                            {pos.cashOutCents !== null
                              ? fmtUsd(pos.cashOutCents / 100)
                              : '—'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditPosition({
                                ticker: pos.ticker,
                                side: pos.side,
                                position: pos.position,
                                pendingSellQty,
                                averagePrice: pos.averagePrice,
                                currentPriceCents: pos.currentPrice,
                              });
                            }}
                            className="text-muted-foreground rounded p-1 transition-colors hover:bg-blue-500/10 hover:text-blue-400"
                            title="Close / edit position"
                          >
                            <Pencil size={12} />
                          </button>
                        </div>
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${
                          pos.ifWinCents >= 0 ? 'text-positive' : 'text-negative'
                        }`}
                      >
                        <div className="flex flex-col items-end leading-tight">
                          <span>
                            {formatDollars(pos.ifWinCents)}{' '}
                            {/* Gross settlement payout (qty × $1) — what
                                actually hits the account on a WIN before
                                netting cost. Settlement has no Kalshi fee. */}
                            <span
                              className="text-muted-foreground"
                              title="Gross settlement payout — qty × $1, no settlement fee"
                            >
                              (${Number(pos.position.toFixed(2))})
                            </span>
                          </span>
                          {(() => {
                            // Implied cost per contract (cents) = totalCost / qty
                            // — fee-adjusted average entry. Feed straight into the
                            // existing americanOdds() helper, which guards 0/100.
                            if (pos.position <= 0) return null;
                            const impliedCents = pos.totalCostCents / pos.position;
                            const odds = americanOdds(impliedCents);
                            return (
                              <span className="text-foreground text-[10px]">{odds}</span>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              )
            ) : (
              <div className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm">
                No open positions
              </div>
            )}
          </div>

          {/* Open Orders — header + summary tiles are constant page
              chrome; only the item shape (rows vs cards) changes. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <h2 className="text-foreground text-sm font-semibold">Open Orders</h2>
                <InfoTooltip title="Open Orders">
                  <OrdersHelp />
                </InfoTooltip>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                {orderViewMode === 'card' && sortedOrders.length > 0 && (
                  <div className="border-border inline-flex overflow-hidden rounded-md border">
                    <SortSegmentButton
                      active={orderSort.key === 'default'}
                      dir={orderSort.dir}
                      label="Default"
                      title="Sort orders by event time, then ticker"
                      onClick={() => handleOrderSort('default')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'size'}
                      dir={orderSort.dir}
                      label="Size"
                      title="Sort orders by working size"
                      onClick={() => handleOrderSort('size')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'queue'}
                      dir={orderSort.dir}
                      label="Queue"
                      title="Sort orders by queue position"
                      onClick={() => handleOrderSort('queue')}
                    />
                    <SortSegmentButton
                      active={orderSort.key === 'velocity'}
                      dir={orderSort.dir}
                      label="Velocity"
                      title="Sort orders by 1m queue/fill movement"
                      onClick={() => handleOrderSort('velocity')}
                    />
                  </div>
                )}
                <OrdersSyncBadge
                  sync={ordersSync}
                  active={accountStatus !== 'disconnected'}
                  onRetry={() => {
                    void refreshOrders();
                    void refreshPolyOrders();
                    bumpHeadroom();
                  }}
                />
                {cancelAllTargetCount > 0 && (
                  <button
                    onClick={() => setCancelAllOpen(true)}
                    disabled={cancelingAll}
                    className="border-red-500/50 bg-red-500/10 text-red-400 hover:bg-red-500/20 mr-1 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors disabled:opacity-60"
                    title="Cancel every open order, both venues — includes orders hidden by the sport filter"
                  >
                    {cancelingAll ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <X size={12} />
                    )}
                    Cancel all ({cancelAllTargetCount})
                  </button>
                )}
                <ViewModeToggle
                  value={orderViewMode}
                  modes={SURFACE_VIEW_MODES}
                  onChange={setOrderViewMode}
                />
              </div>
            </div>
            <OrdersMismatchBanner
              intentContracts={headroom?.intentContracts ?? 0}
              listedCount={cancelAllTargetCount}
              syncFailures={ordersSync.consecutiveFailures}
              syncHealthy={
                ordersSync.consecutiveFailures === 0 &&
                ordersSync.lastSuccessMs !== null &&
                Date.now() - ordersSync.lastSuccessMs < 20_000
              }
              active={accountStatus !== 'disconnected'}
              onRefresh={() => {
                void refreshOrders();
                void refreshPolyOrders();
                bumpHeadroom();
              }}
            />
            <SummaryStrip>
              <SummaryTile label="open orders" value={orderSummary.total} />
              <SummaryTile label="top price" value={orderSummary.atFront} tone="positive" />
              <SummaryTile
                label="queue front"
                value={orderSummary.queueFront}
                tone="positive"
              />
              <SummaryTile label="in sale" value={orderSummary.inSale} tone="warning" />
              <SummaryTile label="behind" value={orderSummary.behind} tone="danger" />
              <SummaryTile label="way behind" value={orderSummary.wayBehind} tone="danger" />
            </SummaryStrip>
            {sortedOrders.length > 0 ? (
              orderViewMode === 'card' ? (
                <CardGrid>
                  {orderCardVMs.map((vm) => {
                    const href = vm.side ? gameDetailHref(vm.ticker, vm.side) : null;
                    const label = vm.subtitle ? `${vm.title} — ${vm.subtitle}` : vm.title;
                    return (
                      <OrderCard
                        key={vm.key}
                        vm={vm}
                        queueCriticalFlash={queueCriticalFlashIds.has(vm.key)}
                        divergence={divergenceByTicker.get(vm.ticker) ?? null}
                        divergenceFlash={divergenceFlashTickers.has(vm.ticker)}
                        detailHref={href}
                        onOpen={href ? () => openOrderPanel(vm.ticker, vm.side) : undefined}
                        canceling={
                          vm.venue === 'kalshi'
                            ? cancelingOrder === vm.cancelId
                            : cancelingPolyOrder === vm.cancelId
                        }
                        onCancel={() =>
                          requestCancelOrder({
                            venue: vm.venue,
                            orderId: vm.cancelId,
                            label,
                          })
                        }
                        onEdit={() => {
                          // vm.title / vm.subtitle combine into the same label
                          // the card already shows in its header — keep the modal
                          // consistent with what the trader was just looking at.
                          // Poly cards have side === null (no YES/NO concept on a
                          // token); fall back to 'yes' as a placeholder badge.
                          setEditOrder({
                            venue: vm.venue,
                            orderId: vm.cancelId,
                            marketLabel: label,
                            side: vm.side ?? 'yes',
                            action: vm.isSell ? 'sell' : 'buy',
                            remainingCount: vm.remainingQty,
                            currentPriceCents: vm.bidCents ?? 50,
                            ticker: vm.ticker,
                            tickSize: vm.tickSize,
                            negRisk: vm.negRisk,
                          });
                        }}
                      />
                    );
                  })}
                </CardGrid>
              ) : (
                  <div className="bg-card border-border rounded-lg border">
                    <table className="w-full">
                  <thead>
                    <tr className="text-muted-foreground border-border border-b text-[11px] font-semibold uppercase tracking-wider">
                      <SortableTh
                        className="px-4 py-2 text-left"
                        sortKey="default"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                      >
                        Market
                      </SortableTh>
                      <th className="w-12 px-2 py-2 text-center">Source</th>
                      <th className="px-3 py-2 text-center">Side</th>
                      <th className="px-3 py-2 text-center">Type</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="size"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                        title="Sort by working order size"
                      >
                        Qty
                      </SortableTh>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Last</th>
                    <SortableTh
                      className="px-3 py-2 text-right"
                      sortKey="velocity"
                      activeSortKey={orderSort.key}
                      activeSortDir={orderSort.dir}
                      onSort={handleOrderSort}
                      title="Sort by 1m order movement. Cell shows side-native price change over the last minute (Kalshi only)."
                    >
                      1m Δ
                    </SortableTh>
                    <th className="px-3 py-2 text-center">Book</th>
                      <SortableTh
                        className="px-3 py-2 text-right"
                        sortKey="queue"
                        activeSortKey={orderSort.key}
                        activeSortDir={orderSort.dir}
                        onSort={handleOrderSort}
                        title="Sort by queue position"
                      >
                        Queue
                      </SortableTh>
                      <th className="px-3 py-2 text-right">Level above</th>
                    <th className="px-3 py-2 text-right">In line behind</th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Working contracts × price = dollars you've committed (parity with the order-card footer)"
                    >
                      Cost
                    </th>
                    <th
                      className="px-3 py-2 text-right"
                      title="Working contracts × (100 − price) = max payout if you win (parity with the order-card footer)"
                    >
                      To Win
                    </th>
                    <th className="w-10 px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {sortedOrders.map((row) => {
                    if (row.kind === 'poly') {
                      // Polymarket rows — Kalshi-only columns (Last / Book /
                      // Queue) render — for poly until per-token hydra book
                      // plumbing exists. Side cell also '—' for now: poly's
                      // yes/no is encoded in the CLOB token ID, not the order
                      // itself, so determining it requires a token→market
                      // resolver we haven't wired yet.
                      const o = row.order;
                      const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
                      const sideStr = String(o.side ?? '').toUpperCase();
                      const isSell = sideStr === 'SELL';
                      const priceFrac =
                        typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
                      const priceCents = Number.isFinite(priceFrac)
                        ? Math.round(priceFrac * 100)
                        : null;
                      const priceOdds =
                        priceCents != null ? americanOdds(priceCents) : null;
                      const orig =
                        typeof o.original_size === 'number'
                          ? o.original_size
                          : Number(o.original_size ?? 0);
                      const matched =
                        typeof o.size_matched === 'number'
                          ? o.size_matched
                          : Number(o.size_matched ?? 0);
                      const remaining = Math.max(0, orig - matched);
                      const filling = matched > 0;
                      const assetId = String(o.asset_id ?? o.market ?? id);
                      // Resolve to a human label via marketStore lookup; fall
                      // back to a short hex preview only when the relevant
                      // sport hasn't loaded yet (next poll re-resolves).
                      const polyDesc = describePolyOrderCached(assetId);
                      const label = polyDesc
                        ? `${polyDesc.matchup} (${polyDesc.betTeam}) ${polyDesc.side.toUpperCase()}`
                        : assetId.length > 14
                          ? `${assetId.slice(0, 6)}…${assetId.slice(-4)}`
                          : assetId;
                      const polyHref = (() => {
                        if (!polyDesc) return null;
                        const [away, home] = polyDesc.matchup.split(' @ ');
                        if (!away || !home) return null;
                        const params = new URLSearchParams({
                          team: polyDesc.betTeam,
                          side: polyDesc.side,
                        });
                        return `/game/${polyDesc.date}/${away}/${home}?${params.toString()}`;
                      })();
                      return (
                        <tr
                          key={`poly-${id}`}
                          className={`border-border/40 hover:bg-muted/30 border-b ${
                            filling ? 'border-l-4 border-l-amber-400/80' : ''
                          } ${polyHref ? 'cursor-pointer' : ''}`}
                          title={assetId}
                          onClick={polyHref ? () => navigate(polyHref) : undefined}
                        >
                          <td className="text-foreground px-4 py-2 font-mono text-xs">
                            {label}
                            {polyHref && <GameDetailLink href={polyHref} className="-my-1 ml-1" />}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <VenueChip venue="polymarket" />
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-center text-xs">
                            —
                          </td>
                          <td className="px-3 py-2 text-center text-xs">
                            <span className="text-foreground">{isSell ? 'ASK' : 'BID'}</span>
                            <span className="text-muted-foreground ml-1">(GTC)</span>
                            {filling && (
                              <span className="ml-1 rounded bg-amber-400/20 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-300">
                                In sale
                              </span>
                            )}
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {remaining}
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            <PriceWithOddsInline cents={priceCents} odds={priceOdds} />
                          </td>
                          {/* Last */}
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          {/* 1m Δ — priceVelocity has no source for Polymarket
                              tickers (marketStore doesn't surface poly mid
                              yet). Dash placeholder for column alignment. */}
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          {/* Book */}
                          <td className="text-muted-foreground px-3 py-2 text-center font-mono text-xs">
                            —
                          </td>
                          {/* Queue, level above, in line behind */}
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          {/* Cost + To Win — mirrors buildPolyOrderVM via the
                              shared openOrderEconomics helper. Polymarket has
                              no maker fee in our model (rate 0), so cost/to-win
                              equal the gross — but routing through the same
                              helper keeps the Kalshi/poly math in lockstep. */}
                          {(() => {
                            const economicQty = filling ? matched : orig;
                            const { costDollars: cost, toWinDollars: toWin } =
                              openOrderEconomics({
                                economicQty,
                                bidCents: priceCents,
                                makerRate: 0,
                                makerRebatePct: 0,
                                isSell,
                              });
                            return (
                              <>
                                <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                                  {cost != null ? fmtUsd(cost) : '—'}
                                </td>
                                <td className="text-positive px-3 py-2 text-right font-mono text-xs">
                                  {toWin != null ? fmtUsd(toWin) : '—'}
                                </td>
                              </>
                            );
                          })()}
                          <td className="px-2 py-2">
                            <OrderActionButtons
                              canceling={cancelingPolyOrder === id}
                              onCancel={() =>
                                requestCancelOrder({
                                  venue: 'polymarket',
                                  orderId: id,
                                  label,
                                })
                              }
                              onEdit={() => {
                                // Side comes from the marketStore lookup when
                                // the market is loaded; falls back to 'yes' as
                                // a benign placeholder for the modal's badge
                                setEditOrder({
                                  venue: 'polymarket',
                                  orderId: id,
                                  marketLabel: label,
                                  side: polyDesc?.side ?? 'yes',
                                  action: isSell ? 'sell' : 'buy',
                                  remainingCount: remaining,
                                  currentPriceCents: priceCents ?? 50,
                                  ticker: assetId,
                                  tickSize: polyDesc?.tickSize,
                                  negRisk: polyDesc?.negRisk,
                                });
                              }}
                            />
                          </td>
                        </tr>
                      );
                    }
                    const order = row.order;
                    const { marketLabel, betTeam } = describeMarket(order.ticker, order.side);
                    const timeLabel = formatStartTimeLocal(row.sortStart);
                    const href = gameDetailHref(order.ticker, order.side);
                    const label = `${marketLabel}${timeLabel ? ` ${timeLabel}` : ''}`;
                    // Kalshi often returns only yes_price even for NO orders; for
                    // a NO bid the actual price the user placed is 100 - yes_price.
                    const bidCents =
                      order.side === 'yes'
                        ? (order.yesPrice ?? (order.noPrice != null ? 100 - order.noPrice : null))
                        : (order.noPrice ?? (order.yesPrice != null ? 100 - order.yesPrice : null));
                    // Last trade comes from hydra (YES-cents); invert for NO orders.
                    const lastYes = getLastTradeYesCents(order.ticker);
                    const lastSide =
                      lastYes == null ? null : order.side === 'yes' ? lastYes : 100 - lastYes;
                    const makerRate = makerRateForTicker(order.ticker);
                    const bidOdds =
                      bidCents == null
                        ? null
                        : americanOdds(
                            effectivePriceForFeeAdjustedOdds(
                              bidCents,
                              feeAdjustedOdds,
                              makerRate,
                              makerRebatePct,
                            ),
                          );
                    const lastOdds =
                      lastSide == null
                        ? null
                        : americanOdds(
                            effectivePriceForFeeAdjustedOdds(
                              Math.round(lastSide),
                              feeAdjustedOdds,
                              makerRate,
                              makerRebatePct,
                            ),
                          );
                    // Book reference depends on the order's direction:
                    //   • BUY (bid)  → max bid on the same side; behind the
                    //                  top means the user's price is below
                    //                  it. Diff = topBid − orderPrice; show
                    //                  Top / −1 / −N (ladder goes down).
                    //   • SELL (ask) → lowest ask on the same side, derived
                    //                  as 100 − max(opposite-side bid). Behind
                    //                  the top means the user's price is above
                    //                  it. Diff = orderPrice − topAsk; show
                    //                  Top / +1 / +N (ladder goes up).
                    void marketVersion;
                    const book = getOrderbookRaw(order.ticker);
                    const isSell = order.action === 'sell';
                    let topRef: number | null = null;
                    if (book) {
                      const refLevels = isSell
                        ? (order.side === 'yes' ? book.no : book.yes)
                        : (order.side === 'yes' ? book.yes : book.no);
                      if (refLevels.length > 0) {
                        let max = -Infinity;
                        for (const lvl of refLevels) {
                          if (lvl.price > max) max = lvl.price;
                        }
                        if (Number.isFinite(max)) {
                          topRef = isSell ? 100 - max : max;
                        }
                      }
                    }
                    const bookDiff =
                      topRef != null && bidCents != null
                        ? Math.max(0, isSell ? bidCents - topRef : topRef - bidCents)
                        : null;
                    const metrics = queueMetricsByOrderId.get(order.orderId);
                    const queue = metrics?.queue ?? null;
                    const queueAbove = metrics?.queueAbove ?? null;
                    const queueBehind = metrics?.queueBehind ?? null;
                    const total = metrics?.total ?? null;
                    // Row tint + value pulse track the cumulative `queue` —
                    // that's the metric a trader's eyes should snap to.
                    // `level above` keeps the queue color scale; `in line
                    // behind` is inverted because more depth behind you is good.
                    const queueTint = queueRowTone(queue, total);
                    const queueTextTone = queueTone(queue, total);
                    const queueAboveTone = queueTone(queueAbove, total);
                    const queueBehindTextTone = queueBehindTone(queueBehind);
                    const queuePulse = queueValuePulse(queue);
                    const filledQty = recentFilledQtyByOrderId.get(order.orderId) ?? 0;
                    const filling = filledQty > 0;
                    const activeInSale = filling && queueAbove === 0;
                    const divergence = divergenceByTicker.get(order.ticker) ?? null;
                    return (
                    <tr
                      key={order.orderId}
                      className={`border-border/40 hover:bg-muted/30 border-b ${queueTint} ${
                        activeInSale ? 'border-l-4 border-l-amber-400/80' : ''
                      } ${href ? 'cursor-pointer' : ''} ${
                        divergenceFlashTickers.has(order.ticker) ? 'animate-divergence-flash' : ''
                      }`}
                      title={order.ticker}
                      onClick={href ? () => openOrderPanel(order.ticker, order.side) : undefined}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        <span
                          className={href ? 'hover:text-blue-400 hover:underline' : undefined}
                        >
                          <BetTeamTitle title={label} team={betTeam} />
                        </span>
                        {href && <GameDetailLink href={href} className="-my-1 ml-1" />}
                        <DivergenceBadge info={divergence} className="ml-1.5 align-middle" />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <VenueChip venue="kalshi" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            order.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {(() => {
                          const sideLabel = order.action === 'sell' ? 'ASK' : 'BID';
                          const exp = order.expirationTs;
                          // No expiration → GTC (rests until cancel or close).
                          // Expiration that lines up with scheduled event
                          // start (±60s tolerance for ISO→sec rounding) → GTS.
                          // Anything else → show local time.
                          let tifLabel = 'GTC';
                          let tifTitle =
                            'Good til close — rests until cancel or contract settlement';
                          if (exp != null) {
                            const expMs = exp * 1000;
                            const isGts =
                              row.sortStart > 0 &&
                              Math.abs(expMs - row.sortStart) <= 60_000;
                            if (isGts) {
                              tifLabel = 'GTS';
                              tifTitle = `Good to Start — auto-cancels at scheduled event start (${new Date(expMs).toLocaleString()})`;
                            } else {
                              tifLabel = new Date(expMs).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                              });
                              tifTitle = `Auto-cancels at ${new Date(expMs).toLocaleString()}`;
                            }
                          }
                          return (
                            <span title={tifTitle}>
                              <span className="text-foreground">{sideLabel}</span>
                              <span className="text-muted-foreground ml-1">({tifLabel})</span>
                              {activeInSale && (
                                <span className="ml-1 rounded bg-amber-400/20 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-300">
                                  In sale
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {order.remainingCount}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        <PriceWithOddsInline cents={bidCents} odds={bidOdds} />
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        <PriceWithOddsInline
                          cents={lastSide != null ? Math.round(lastSide) : null}
                          odds={lastOdds}
                          muted
                        />
                      </td>
                      {/* 1m Δ — same side-native chip math as the OrderCard,
                          surfaced as a table cell for card↔table parity. */}
                      {(() => {
                        const yesM1 = priceVelocity(order.ticker).m1.priceDelta;
                        const delta = sideNativePriceDelta(yesM1, order.side);
                        if (delta == null) {
                          return (
                            <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                              —
                            </td>
                          );
                        }
                        const tone =
                          delta > 0
                            ? 'text-green-400'
                            : delta < 0
                              ? 'text-red-400'
                              : 'text-muted-foreground';
                        return (
                          <td className={`px-3 py-2 text-right font-mono text-xs ${tone}`}>
                            {delta >= 0 ? '+' : ''}{delta}¢
                          </td>
                        );
                      })()}
                      <td className="px-3 py-2 text-center font-mono text-xs">
                        {bookDiff == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1.5 ${
                              bookDiff === 0
                                ? 'text-green-400'
                                : bookDiff === 1
                                  ? 'text-yellow-400'
                                  : 'text-red-400'
                            }`}
                            title={
                              topRef != null
                                ? `Top ${isSell ? 'ask' : 'bid'} price ${topRef}¢; queue shows FIFO depth at this price`
                                : undefined
                            }
                          >
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                bookDiff === 0
                                  ? 'bg-green-400'
                                  : bookDiff === 1
                                    ? 'bg-yellow-400'
                                    : 'bg-red-400'
                              }`}
                            />
                            {bookDiff === 0 ? 'Top' : `${isSell ? '+' : '-'}${bookDiff}`}
                          </span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${queueTextTone} ${queuePulse}`}>
                        {queue == null
                          ? '—'
                          : queue < 1
                            ? 'Front'
                            : Math.round(queue).toLocaleString()}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-xs ${queueAboveTone}`}>
                        {queueAbove == null
                          ? '—'
                          : Math.round(queueAbove).toLocaleString()}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${queueBehindTextTone}`}
                      >
                        {queueBehind == null
                          ? '—'
                          : Math.round(queueBehind).toLocaleString()}
                      </td>
                      {/* Cost + To Win — parity with OrderCard footer. Uses
                          the shared openOrderEconomics helper so the fee-
                          inclusive math stays in lockstep with the card view:
                          a resting limit bills as a maker fill, so the per-
                          series maker fee folds into cost (+fee) / to-win
                          (−fee). bidCents is side-native already. */}
                      {(() => {
                        const economicQty = filling ? filledQty : order.remainingCount;
                        const { costDollars: cost, toWinDollars: toWin } =
                          openOrderEconomics({
                            economicQty,
                            bidCents,
                            makerRate: makerRateForTicker(order.ticker),
                            makerRebatePct,
                            isSell,
                          });
                        return (
                          <>
                            <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                              {cost != null ? fmtUsd(cost) : '—'}
                            </td>
                            <td className="text-positive px-3 py-2 text-right font-mono text-xs">
                              {toWin != null ? fmtUsd(toWin) : '—'}
                            </td>
                          </>
                        );
                      })()}
                      <td className="px-2 py-2">
                        <OrderActionButtons
                          canceling={cancelingOrder === order.orderId}
                          onCancel={() =>
                            requestCancelOrder({
                              venue: 'kalshi',
                              orderId: order.orderId,
                              label,
                            })
                          }
                          onEdit={() => {
                            // bidCents is the side-native current price computed
                            // a few lines up. Fall back to 50¢ midpoint only if
                            // the orderbook hasn't seeded it yet — the trader
                            // will overtype anyway.
                            setEditOrder({
                              venue: 'kalshi',
                              orderId: order.orderId,
                              marketLabel: betTeam
                                ? `${marketLabel} (${betTeam})`
                                : marketLabel,
                              side: order.side,
                              action: order.action,
                              remainingCount: order.remainingCount,
                              currentPriceCents: bidCents ?? 50,
                              ticker: order.ticker,
                            });
                          }}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              )
            ) : (
              <div className="bg-card border-border text-muted-foreground rounded-lg border px-4 py-8 text-center text-sm">
                No open orders
              </div>
            )}
          </div>
          {/* Bot zones — "My Bots" (claimed) + the org pool — lead the footer:
              the roster below is a convenience, the bots are the main event.
              Self-gating: only renders for bot-access traders/managers with bots. */}
          <BotZones />
          {footer}
        </div>

        {/* Buy window — the dashboard's GameDetailPanel docked as a right
            rail. Takes the tracker's slot while open (both at once crowds the
            positions table off the screen); closing restores the tracker.
            Keyed by game so switching games remounts cleanly. */}
        {buyPanel && buyPanelGame && (
          /* sticky + self-start: the tracker page is one big scroll
             container, so without this the panel sits in-flow and a click
             far down the position list opens the book off-screen above
             (Prof's Loom, Jun 12 — "fails when scrolled"). Same pattern as
             the tracker below; the height cap makes the panel's own
             books section scroll internally instead of growing the page. */
          <div className="sticky top-0 h-[calc(100vh-3rem)] shrink-0 self-start">
            <ExpandedGamePanel
              key={buyPanelGame.key}
              game={buyPanelGame}
              onClose={() => setBuyPanel(null)}
              tradeIntent={buyPanel.intent}
              marketSide={panelMarketSide}
              onTeamSideChange={(side) => {
                setBuyPanel((bp) => (bp ? { ...bp, intent: { ...bp.intent, side } } : null));
              }}
              betLineContext={null}
            />
          </div>
        )}

        {!buyPanel && (trackerDocked || fillsDocked) && (
          /* sticky + self-start (same proven pattern as the buy panel above):
             self-start stops the aside stretching so the rail pins and stays in
             view as the list scrolls. Docked prongs stack and share the height;
             one docked prong fills the rail. Viewport-capped — each docked
             PositionSidebar scrolls internally. */
          <aside
            className="sticky top-0 hidden h-[calc(100vh-3rem)] w-80 shrink-0 flex-col gap-3 self-start lg:flex"
            aria-label={
              trackerDocked && fillsDocked
                ? 'Position tracker and recent fills'
                : trackerDocked
                  ? 'Position tracker'
                  : 'Recent fills'
            }
          >
            {trackerDocked && (
              <div className="min-h-0 flex-1">
                <PositionSidebar
                  variant="docked"
                  section="tracker"
                  onClose={() => setTrackerHidden(true)}
                  onPopOut={popTracker}
                />
              </div>
            )}
            {fillsDocked && (
              <div className="min-h-0 flex-1">
                <PositionSidebar
                  variant="docked"
                  section="fills"
                  onClose={() => setFillsHidden(true)}
                  onPopOut={popFills}
                />
              </div>
            )}
          </aside>
        )}
      </div>

      {cancelError && (
        <div
          className="fixed right-6 bottom-6 z-50 max-w-sm rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300 shadow-lg"
          role="alert"
        >
          <div className="flex items-start justify-between gap-3">
            <span>{cancelError}</span>
            <button
              type="button"
              onClick={() => setCancelError(null)}
              className="text-red-300/70 hover:text-red-200"
              aria-label="Dismiss cancel error"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={pendingCancelOrder !== null}
        onClose={() => setPendingCancelOrder(null)}
        onConfirm={() => {
          if (pendingCancelOrder) void executeCancelOrder(pendingCancelOrder);
        }}
        title="Cancel order?"
        message={
          pendingCancelOrder
            ? `Cancel ${pendingCancelOrder.label}?`
            : 'Cancel this order?'
        }
        confirmLabel="Cancel order"
        cancelLabel="Back"
        variant="danger"
      />

      <ConfirmModal
        isOpen={cancelAllOpen}
        onClose={() => setCancelAllOpen(false)}
        onConfirm={() => {
          setCancelAllOpen(false);
          void handleCancelAllOrders();
        }}
        title="Cancel ALL open orders?"
        message={`Cancel all ${cancelAllTargetCount} open order${cancelAllTargetCount === 1 ? '' : 's'} (Kalshi + Polymarket)${sportFilter.size > 0 ? ' — including orders hidden by your sport filter' : ''}? This cannot be undone.`}
        confirmLabel={`Cancel all ${cancelAllTargetCount} order${cancelAllTargetCount === 1 ? '' : 's'}`}
        cancelLabel="Back"
        variant="danger"
      />

      {editPosition && (
        <EditPositionModal
          data={editPosition}
          onClose={() => setEditPosition(null)}
          onPlaced={() => {
            // Nudge the next account refresh and P&L-card display tick so the
            // closed contracts disappear without a perceived lag.
            refreshPositions();
            refreshOrders();
            setDisplayTotalPnl(latestVisibleTotalPnlRef.current);
            setPnlCardAnchorMs(Date.now());
          }}
        />
      )}

      {editOrder && (
        <EditOrderPriceModal
          isOpen={editOrder !== null}
          onClose={() => setEditOrder(null)}
          onReplace={handleReplaceFromModal}
          marketLabel={editOrder.marketLabel}
          side={editOrder.side}
          action={editOrder.action}
          remainingCount={editOrder.remainingCount}
          currentPriceCents={editOrder.currentPriceCents}
          ticker={editOrder.ticker}
          venue={editOrder.venue}
        />
      )}
    </div>
  );
}

// ── Manager view: own data + per-trader summary ─────────────────

function ManagerPositionsView({
  currentUserId,
  onSelectTrader,
}: {
  currentUserId: string | null;
  onSelectTrader?: (row: OrgTraderRow) => void;
}) {
  return (
    <div className="h-full overflow-auto">
      <div className="px-6 pb-6">
        <TraderView
          footer={
            <OrgTradersTable
              highlightUserId={currentUserId ?? undefined}
              onRowClick={onSelectTrader}
              title="My Traders"
            />
          }
        />
      </div>
    </div>
  );
}

// ── Admin view: per-org summary ─────────────────────────────────

interface OrgSummaryRow {
  orgId: string;
  name: string;
  hasKeys: boolean;
  openCount: number;
  openNotionalCents: number;
  openOrderCount: number;
  realizedPnlCents: number;
}

// An admin spans every org, so (unlike the manager view) this adds an
// OrgSwitcher — the same shared useOrgScope control used on Org Account / Org
// Settings — to choose which org's traders to inspect. Clicking a trader drills
// in scoped to that org (PositionsRoute threads the selected orgId). Additive
// over the prior admin /positions: TraderView is preserved.
function AdminPositionsView({
  currentUserId,
  onSelectTrader,
}: {
  currentUserId: string | null;
  onSelectTrader?: (row: OrgTraderRow) => void;
}) {
  const selectedOrgId = useOrgScope((s) => s.selectedOrgId);
  const navigate = useNavigate();
  return (
    <div className="h-full overflow-auto">
      <div className="px-6 pt-4 pb-6">
        {/* Org scope is a page-level control, so it leads the view. */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wider">
            Viewing
          </span>
          <OrgSwitcher />
        </div>
        <TraderView
          footer={
            <OrgTradersTable
              orgQueryParam={selectedOrgId ? `?orgId=${selectedOrgId}` : ''}
              highlightUserId={currentUserId ?? undefined}
              onRowClick={onSelectTrader}
              title="Org Traders"
              onViewOrgAccount={
                selectedOrgId ? () => navigate(`/positions/org/${selectedOrgId}`) : undefined
              }
            />
          }
        />
      </div>
    </div>
  );
}

export type { OrgSummaryRow };
